import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import {
  FileText,
  ArrowLeft, Loader2, Save, Trash2, GripVertical,
  Maximize2, Minimize2, Database, Pencil, Plus, X, ChevronRight, ChevronDown,
  ChevronsDownUp, ChevronsUpDown, Filter, Table2, Eye,
} from 'lucide-react'
import { TypeChip, eyebrowStyle } from '@/pages/workbench/analytics/shared'
import type { EmbeddedDesignerProps } from './embeddedDesigner'
import {
  loadVisualObject, saveVisualObject,
  listVisualSchemas, loadVisualSchema, type VisualSchemaSummary,
  listBusinessAreas, loadBusinessArea, type BusinessAreaSummary,
} from '@/api/designers'
import { useProjectStore } from '@/store/projectStore'
import { useBuilderContextStore } from '@/store/builderContextStore'
import { useUnsavedChangesGuard } from '@/hooks/useUnsavedChangesGuard'
import { useAgentDeletion, useAgentRefresh } from '@/hooks/useAgentRefresh'
import { useDesignHistory } from '@/hooks/useDesignHistory'
import UndoRedoButtons from '@/components/common/UndoRedoButtons'
import DiscardChangesModal from '@/components/common/DiscardChangesModal'
import { MapBoundaryStatus } from '@/components/geo/MapBoundaries'
import SchemaDesignerPage from './SchemaDesignerPage'
import DocumentFormDesignerPage from './DocumentFormDesignerPage'
import { isDocumentVisual as isDocumentVisualType } from '@twasta/visual-engine'
import { DocumentOutputPanel } from './visual/DocumentOutputPanel'
import {
  VISUAL_CATALOG, loadVisualCatalog,
  type VisualCatalogEntry,
} from './visual/catalog'
import type {
  VisualModel, VisualColumn, CriteriaNode, CriteriaRule, CriteriaGroup,
  VisualOption, VisualSchema,
} from './visual/types'
import { isCriteriaGroup, criteriaLeaves, criteriaGroupAtPath } from './visual/types'
import {
  blankVisualModel, sanitizeVisualModel,
} from './visual/helpers'
import {
  availableColumns, groupByTable, bankKeyOf, materializeColumn,
  deriveSqlCriteria, schemaColumnToCriteria, syncMasterFromGroups, setSqlBinding,
  type BankColumn, type ResolvedSqlColumn,
} from './visual/dataSource'
import { parseSourceSql } from './visual/sqlParser'
import { fetchSqlColumns, fetchVisualFunctions, type VisualFunction } from '@/api/visualData'
import DisplayFormatEditor from './transaction/DisplayFormatEditor'
import type { ColumnDef, DisplayFormat } from '@twasta/page-runtime/txn'
import SortTopNPanel from './visual/SortTopNPanel'
import FunctionCriteriaCard from './visual/FunctionCriteriaCard'
import CriteriaCard from './visual/CriteriaCard'
import VisualPreviewModal from './visual/VisualPreviewModal'
import VisualTypePicker from './visual/VisualTypePicker'
import { listProjectVariables, type ProjectVariable } from '@/api/projectVariables'
import { useProjectPermissions } from '@/auth/useProjectPermissions'
import { ReadOnlyBadge } from '@/auth/ReadOnlyBadge'
import { useRunOnlyRedirect } from '@/auth/useRunOnlyRedirect'

const COLTYPE_OPTIONS = ['CHAR', 'NUMBER', 'DATE'] as const
const TYPE_OPTIONS = ['string', 'number', 'date'] as const
// Fallback function list while the dialect catalog loads (or the endpoint is
// unreachable) — only functions every supported database runs.
const PORTABLE_FUNCTIONS: VisualFunction[] = [
  { name: 'SUM', label: 'Sum', kind: 'aggregate' },
  { name: 'AVG', label: 'Average', kind: 'aggregate' },
  { name: 'COUNT', label: 'Count', kind: 'aggregate' },
  { name: 'COUNT_DISTINCT', label: 'Count distinct', kind: 'aggregate' },
  { name: 'MIN', label: 'Minimum', kind: 'aggregate' },
  { name: 'MAX', label: 'Maximum', kind: 'aggregate' },
]

// Client-side pivot aggregations — mirrors ProPivot's ALL_AGGREGATIONS +
// AGGREGATION_CAPTIONS (packages/../ProPivot/src/core/aggregations.ts:131-155).
// Hardcoded locally so the designer main chunk never imports @proteus/propivot.
const PIVOT_AGG_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: '(default — from Aggregation)' },
  { value: 'sum', label: 'Sum' },
  { value: 'count', label: 'Count' },
  { value: 'distinctcount', label: 'Distinct Count' },
  { value: 'average', label: 'Average' },
  { value: 'median', label: 'Median' },
  { value: 'product', label: 'Product' },
  { value: 'min', label: 'Min' },
  { value: 'max', label: 'Max' },
  { value: 'percent', label: '% of Grand Total' },
  { value: 'percentofcolumn', label: '% of Column' },
  { value: 'percentofrow', label: '% of Row' },
  { value: 'index', label: 'Index' },
  { value: 'difference', label: 'Difference' },
  { value: '%difference', label: '% Difference' },
  { value: 'stdevp', label: 'Population StdDev' },
  { value: 'stdevs', label: 'Sample StdDev' },
  { value: 'runningtotals', label: 'Running Totals' },
]
// Positional family — honours a PIVOT_AXIS direction.
const POSITIONAL_PIVOT_AGGS = new Set(['difference', '%difference', 'runningtotals'])

type Selection =
  | { kind: 'none' }
  | { kind: 'column'; colId: number }

// Drag payloads carried across the Layout column bank ↔ groups and the
// schema tree ↔ criteria drop-zone.
type DragItem =
  | { kind: 'bank'; key: string }     // an unplaced data-source column
  | { kind: 'col'; colId: number }    // a column already placed in a group

/** Pull a VisualSchema out of whatever shape the loader returned. */
function extractSchema(content: unknown): VisualSchema | null {
  if (!content || typeof content !== 'object') return null
  const c = content as Record<string, unknown>
  const inner = (c.SCHEMA as Record<string, unknown>) || c
  const tables = Array.isArray(inner.TABLES) ? inner.TABLES : []
  return {
    SCHEMA_NAME: String(inner.SCHEMA_NAME || ''),
    SCHEMA_DESCR: String(inner.SCHEMA_DESCR || ''),
    SCHEMA_TYPE: String(inner.SCHEMA_TYPE || 'S'),
    CONNECTION_ID: String(inner.CONNECTION_ID || ''),
    TABLES: tables as VisualSchema['TABLES'],
    JOINS: Array.isArray(inner.JOINS) ? (inner.JOINS as VisualSchema['JOINS']) : [],
    COLUMN_GROUPS: Array.isArray(inner.COLUMN_GROUPS)
      ? (inner.COLUMN_GROUPS as VisualSchema['COLUMN_GROUPS'])
      : [],
  }
}

export default function VisualDesignerPage({
  embedded = false,
  hostFullscreen = false,
  embedProject,
  embedObjName,
  onDirtyChange,
  registerSave,
  onSaved,
}: EmbeddedDesignerProps = {}) {
  const params = useParams<{ project: string; objName: string }>()
  const project = embedProject ?? params.project
  useRunOnlyRedirect(project)
  const objName = embedObjName ?? params.objName
  const navigate = useNavigate()
  const location = useLocation()
  const projectName = project || ''
  const objectName = objName || ''
  const { currentProject, selectProject } = useProjectStore()

  useEffect(() => {
    if (project && currentProject?.application.folder_name !== project) {
      selectProject(project)
    }
  }, [project, currentProject, selectProject])

  const [model, setModel] = useState<VisualModel>(() => blankVisualModel(objectName))
  const [projectVars, setProjectVars] = useState<ProjectVariable[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const { canEditObjectsOrLoading: canEdit } = useProjectPermissions()
  const [selection, setSelection] = useState<Selection>({ kind: 'none' })
  const [dragItem, setDragItem] = useState<DragItem | null>(null)
  const [tab, setTab] = useState<'source' | 'layout' | 'options'>('source')
  // Default to showing EVERY option applicable to the selected visual type,
  // each with an editable input and a Customized/Default indicator. Users can
  // still narrow to just their overrides via the "Overrides only" toggle.
  const [showOverridesOnly, setShowOverridesOnly] = useState(false)
  const [embedFullscreen, setEmbedFullscreen] = useState(false)
  // Live preview of the current (possibly unsaved) model — rendered in the
  // shared, standalone VisualPreviewModal (outside the designer tree) so the
  // same preview surface is reused by the card "View" and app navigation.
  const [previewOpen, setPreviewOpen] = useState(false)

  // ── Visual schema (Schema-mode data source) ──────────────────────
  const [schemaList, setSchemaList] = useState<VisualSchemaSummary[]>([])
  const [schemaData, setSchemaData] = useState<VisualSchema | null>(null)
  const [schemaBusy, setSchemaBusy] = useState(false)
  // Business Areas — the analytics basis a visual can be built on.
  // 'Default' (blank) = no area: the visual reads the project DB / its own
  // schema exactly as before, with no area definition required.
  const [areaList, setAreaList] = useState<BusinessAreaSummary[]>([])
  // The Business Area's referenced schema name (when the area delegates its
  // dataset to a Visual Schema) — display-only.
  const [areaSchemaName, setAreaSchemaName] = useState('')
  const [editSchemaOpen, setEditSchemaOpen] = useState(false)
  // Bumped to force a schema reload (after the in-place editor closes).
  const [schemaNonce, setSchemaNonce] = useState(0)
  // The document designer, opened in place exactly as the schema designer is.
  const [editDocumentOpen, setEditDocumentOpen] = useState(false)
  const [documentNonce, setDocumentNonce] = useState(0)

  const [savedSnapshot, setSavedSnapshot] = useState<string | null>(null)
  const isDirty = savedSnapshot !== null && JSON.stringify(model) !== savedSnapshot
  const { guard, showDiscardModal, confirmDiscard, cancelDiscard } =
    useUnsavedChangesGuard(isDirty)
  const history = useDesignHistory(model, setModel, { disabled: !canEdit })
  const { reset: resetHistory } = history

  const agentRefresh = useAgentRefresh(projectName, isDirty)

  // An agent run that DELETED this object must close it, not refetch it — a
  // refetch 404s and leaves the removed object on screen (and a later Save
  // would resurrect it). Deletions only became visible to the client in Phase
  // 0.8 of docs/ssh_agent_console_support_plan.md.
  useAgentDeletion(projectName, objectName, 'visual_models', () => {
    if (embedded) return   // the host tab owns navigation
    navigate('/workbench/analytics')
  })

  // Publish the open visual as the Builder/Ask-AI "open object" so the agent
  // resolves "this visual" / "this chart" / "this" / "here" to it instead of
  // guessing — parity with the nav tree and workflow designer.
  useEffect(() => {
    if (!objectName) return
    useBuilderContextStore.getState().setOpenObject({
      obj_name: objectName,
      obj_type: 'V',
      title: objectName,
    })
    return () => useBuilderContextStore.getState().setOpenObject(null)
  }, [objectName])

  useEffect(() => {
    if (!projectName) { setProjectVars([]); return }
    let cancelled = false
    listProjectVariables(projectName)
      .then((vars) => { if (!cancelled) setProjectVars(vars) })
      .catch(() => { if (!cancelled) setProjectVars([]) })
    return () => { cancelled = true }
  }, [projectName])

  // Schema catalogue (names) — for the Data Source schema picker dropdown.
  useEffect(() => {
    if (!projectName) { setSchemaList([]); return }
    let cancelled = false
    listVisualSchemas(projectName)
      .then((res) => { if (!cancelled) setSchemaList(res?.schemas || []) })
      .catch(() => { if (!cancelled) setSchemaList([]) })
    return () => { cancelled = true }
  }, [projectName, schemaNonce])

  // Business Area catalogue — for the "Based on" selector.
  useEffect(() => {
    if (!projectName) { setAreaList([]); return }
    let cancelled = false
    listBusinessAreas(projectName)
      .then((res) => { if (!cancelled) setAreaList(res?.business_areas || []) })
      .catch(() => { if (!cancelled) setAreaList([]) })
    return () => { cancelled = true }
  }, [projectName])

  // Load the visual catalog + the visual model, then sanitize.
  useEffect(() => {
    let cancelled = false
    if (!projectName || !objectName) return
    setLoading(true)
    Promise.all([
      loadVisualCatalog(),
      loadVisualObject(projectName, objectName).catch((err) => {
        console.error('Failed to load visual:', err)
        toast.error('Failed to load visual')
        return null
      }),
    ])
      .then(([, data]) => {
        if (cancelled) return
        const loaded = data && data.content
          ? sanitizeVisualModel(data.content as VisualModel, objectName)
          : blankVisualModel(objectName)
        // SQL mode: reconcile criteria with the SQL's ?. placeholders on open
        // so the panel reflects the query without waiting for an edit. Snapshot
        // is taken afterwards, so this silent normalization isn't "dirty".
        if ((loaded.DATA_SOURCE_MODE || 'sql') === 'sql') {
          loaded.SQLMODEL.CRITERIA.query.rules = deriveSqlCriteria(
            loaded.SOURCE_SQL || '', loaded.SQLMODEL.CRITERIA.query.rules,
          ).rules
        }
        setModel(loaded)
        setSavedSnapshot(JSON.stringify(loaded))
        // A (re)load replaces the model wholesale — stale undo entries would
        // restore a different/older visual.
        resetHistory()
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [projectName, objectName, agentRefresh, resetHistory])

  // Load the dataset (tables/columns) when in schema mode. With a Business
  // Area selected the dataset comes from the AREA — its referenced Visual
  // Schema when set, else the area's own tables/joins (the area body reuses
  // the schema shape). Without an area (Default) the visual's own schema
  // reference loads exactly as before.
  const schemaName = model.SQLMODEL.SCHEMA_NAME || ''
  const businessArea = (model.SQLMODEL.BUSINESS_AREA || '').trim()
  const mode = model.DATA_SOURCE_MODE || 'sql'
  useEffect(() => {
    if (mode !== 'sqlmodel' || !projectName || (!schemaName && !businessArea)) {
      setSchemaData(null)
      setAreaSchemaName('')
      return
    }
    let cancelled = false
    setSchemaBusy(true)
    const loadDataset = async (): Promise<VisualSchema | null> => {
      if (businessArea) {
        const res = await loadBusinessArea(projectName, businessArea)
        const raw = res?.content as Record<string, unknown> | null
        const inner = (raw?.BUSINESS_AREA as Record<string, unknown>) || raw
        if (!inner) return null
        const ref = String(inner.SCHEMA_NAME || '').trim()
        if (!cancelled) setAreaSchemaName(ref)
        if (ref) {
          const s = await loadVisualSchema(projectName, ref)
          if (s?.content) return extractSchema(s.content)
        }
        return inner as unknown as VisualSchema
      }
      if (!cancelled) setAreaSchemaName('')
      const res = await loadVisualSchema(projectName, schemaName)
      return res?.content ? extractSchema(res.content) : null
    }
    loadDataset()
      .then((data) => { if (!cancelled) setSchemaData(data) })
      .catch(() => { if (!cancelled) setSchemaData(null) })
      .finally(() => { if (!cancelled) setSchemaBusy(false) })
    return () => { cancelled = true }
  }, [mode, projectName, schemaName, businessArea, schemaNonce])

  const catalogEntry: VisualCatalogEntry = useMemo(() => {
    return (
      VISUAL_CATALOG.find((c) => c.VisualName === model.SQLMODEL.VISUAL_NAME) ||
      VISUAL_CATALOG[0]
    )
  }, [model.SQLMODEL.VISUAL_NAME])

  /**
   * This visual's output is a laid-out DOCUMENT rather than a chart.
   *
   * Read from `className`, which is what every renderer already dispatches on
   * — the catalog's `OutputType` is stored but has never been consulted at
   * render time, and making this the one place it suddenly mattered would put
   * two answers in the tree to the same question.
   */
  const isDocumentVisual = isDocumentVisualType(
    catalogEntry.className, catalogEntry.ID)

  // ── Mutators ────────────────────────────────────────────────
  const updateModel = useCallback(
    (mutator: (m: VisualModel) => void) => {
      setModel((prev) => {
        const next = JSON.parse(JSON.stringify(prev)) as VisualModel
        mutator(next)
        return next
      })
    },
    []
  )

  const handleVisualChange = useCallback(
    (visualName: string) => {
      const entry = VISUAL_CATALOG.find((c) => c.VisualName === visualName)
      if (!entry) return
      updateModel((m) => {
        m.SQLMODEL.VISUAL_NAME = entry.VisualName
        m.LAYOUT_DATA.visualLayout.ID = entry.ID
        m.LAYOUT_DATA.visualLayout.VisualName = entry.VisualName
        m.LAYOUT_DATA.visualLayout.VisualIcon = entry.VisualIcon
        m.LAYOUT_DATA.visualLayout.DisplayOrder = entry.DisplayOrder
        m.LAYOUT_DATA.visualLayout.className = entry.className
        m.LAYOUT_DATA.visualLayout.OutputType = entry.OutputType
        m.LAYOUT_DATA.visualLayout.linkOptions = ''
        const existingCols = new Map<string, VisualColumn[]>()
        for (const g of m.LAYOUT_DATA.visualLayout.ColumnGroups || []) {
          // Case-insensitive, as in helpers.ts: an app-server visual says "rows".
          existingCols.set(String(g.StandardName || '').toLowerCase(), g.COLUMNS || [])
        }
        m.LAYOUT_DATA.visualLayout.ColumnGroups = entry.ColumnGroups.map(
          (gs, i) => ({
            GroupID: i + 1,
            GroupName: gs.GroupName,
            GroupDescription: gs.GroupDescription,
            GroupIcon: gs.GroupIcon,
            AllowedColumnTypes: gs.AllowedColumnTypes,
            MinColumns: gs.MinColumns,
            MaxColumns: gs.MaxColumns,
            StandardName: gs.StandardName,
            COLUMNS: existingCols.get(gs.StandardName.toLowerCase()) || [],
          })
        )
        m.LAYOUT_DATA.visualLayout.options = entry.options.map((o) => ({
          optionId: o.optionId,
          optionName: o.optionName,
          optionHeading: o.optionHeading,
          dataType: o.dataType,
          defaultValue: o.defaultValue,
          listOfValues: o.listOfValues,
          allowChange: o.allowChange,
          optionType: o.optionType,
          showWhen: o.showWhen,
        }))
        syncMasterFromGroups(m)
      })
    },
    [updateModel]
  )

  // Mode toggle. Switching to SQL re-derives criteria from the SQL; switching
  // to schema keeps any visually-built rules.
  const handleModeChange = useCallback(
    (next: 'sql' | 'sqlmodel') => {
      updateModel((m) => {
        m.DATA_SOURCE_MODE = next
        if (next === 'sql') {
          const { rules } = deriveSqlCriteria(m.SOURCE_SQL || '', m.SQLMODEL.CRITERIA.query.rules)
          m.SQLMODEL.CRITERIA.query.rules = rules
        } else {
          // A schema visual has no query of its own — the engine builds one
          // from the schema. Keeping the old text would leave a query nothing
          // in the designer shows, and the app server RUNS a stored SOURCE_SQL
          // in preference to the schema, so a deploy would answer with the
          // visual's previous question.
          m.SOURCE_SQL = ''
        }
        // Layout placements may reference the other source — clear them so the
        // bank reflects the new data source cleanly.
        for (const g of m.LAYOUT_DATA.visualLayout.ColumnGroups || []) g.COLUMNS = []
        syncMasterFromGroups(m)
      })
      setSelection({ kind: 'none' })
    },
    [updateModel]
  )

  const handleSourceSqlChange = useCallback(
    (value: string) => {
      updateModel((m) => {
        m.SOURCE_SQL = value
        if ((m.DATA_SOURCE_MODE || 'sql') === 'sql') {
          const { rules } = deriveSqlCriteria(value, m.SQLMODEL.CRITERIA.query.rules)
          m.SQLMODEL.CRITERIA.query.rules = rules
        }
      })
    },
    [updateModel]
  )

  const handleSchemaNameChange = useCallback(
    (name: string) => {
      // Keep the author's casing — schema files are case-sensitive on the
      // server (the file name IS the schema id). Only strip unsafe chars.
      const clean = name.trim().replace(/[^A-Za-z0-9_-]/g, '_')
      updateModel((m) => {
        const summary = schemaList.find(
          (s) => s.schema_name.toUpperCase() === clean.toUpperCase()
        )
        // Picking a known schema (any casing) snaps to its exact stored name.
        m.SQLMODEL.SCHEMA_NAME = summary ? summary.schema_name : clean
        if (summary) m.SQLMODEL.SCHEMA_DESCR = summary.schema_descr
        // New schema → clear stale placements.
        for (const g of m.LAYOUT_DATA.visualLayout.ColumnGroups || []) g.COLUMNS = []
        syncMasterFromGroups(m)
      })
      setSelection({ kind: 'none' })
    },
    [updateModel, schemaList]
  )

  const handleBusinessAreaChange = useCallback(
    async (name: string) => {
      // The Business Area drives the data source: an area WITH a schema
      // attached snaps the visual to Schema mode on that schema; an area
      // WITHOUT one starts from SQL Query (freehand SQL on the area's
      // connection). Default keeps whatever the author had.
      let refSchema = ''
      if (name) {
        try {
          const res = await loadBusinessArea(projectName, name)
          const raw = res?.content as Record<string, unknown> | null
          const inner = (raw?.BUSINESS_AREA as Record<string, unknown>) || raw
          refSchema = String(inner?.SCHEMA_NAME || '').trim()
        } catch { /* area unreadable — leave the mode unchanged */ }
      }
      updateModel((m) => {
        m.SQLMODEL.BUSINESS_AREA = name
        if (name) {
          if (refSchema) {
            m.DATA_SOURCE_MODE = 'sqlmodel'
            m.SQLMODEL.SCHEMA_NAME = refSchema
          } else {
            m.DATA_SOURCE_MODE = 'sql'
          }
        }
        // The dataset changed → stale placements would reference old columns.
        for (const g of m.LAYOUT_DATA.visualLayout.ColumnGroups || []) g.COLUMNS = []
        syncMasterFromGroups(m)
      })
      setSelection({ kind: 'none' })
    },
    [updateModel, projectName]
  )

  // ── Column placement (Layout) ─────────────────────────────────
  const placeBankColumn = useCallback(
    (bankKey: string, targetStdName: string, bank: BankColumn[]) => {
      const src = bank.find((b) => b.key === bankKey)
      if (!src) return
      updateModel((m) => {
        // Already placed? move it instead of duplicating.
        let existing: VisualColumn | undefined
        for (const g of m.LAYOUT_DATA.visualLayout.ColumnGroups || []) {
          const found = (g.COLUMNS || []).find(
            (c) => bankKeyOf(c, m.DATA_SOURCE_MODE) === bankKey
          )
          if (found) { existing = found; g.COLUMNS = g.COLUMNS.filter((c) => c !== found) }
        }
        const allCols: VisualColumn[] = []
        for (const g of m.LAYOUT_DATA.visualLayout.ColumnGroups || []) allCols.push(...(g.COLUMNS || []))
        const nextId = allCols.length === 0 ? 2 : Math.max(...allCols.map((c) => c.COLID || 0), 1) + 1
        const col = existing || materializeColumn(src, nextId)
        const target = (m.LAYOUT_DATA.visualLayout.ColumnGroups || []).find(
          (g) => g.StandardName === targetStdName
        )
        if (target) {
          col.groupName = target.GroupName
          col.StandardName = target.StandardName
          target.COLUMNS = [...(target.COLUMNS || []), col]
        }
        syncMasterFromGroups(m)
      })
    },
    [updateModel]
  )

  const moveColumn = useCallback(
    (colId: number, targetStdName: string | null) => {
      updateModel((m) => {
        let moving: VisualColumn | undefined
        for (const g of m.LAYOUT_DATA.visualLayout.ColumnGroups || []) {
          const found = (g.COLUMNS || []).find((c) => c.COLID === colId)
          if (found) { moving = found; g.COLUMNS = g.COLUMNS.filter((c) => c.COLID !== colId) }
        }
        if (moving && targetStdName) {
          const target = (m.LAYOUT_DATA.visualLayout.ColumnGroups || []).find(
            (g) => g.StandardName === targetStdName
          )
          if (target) {
            moving.groupName = target.GroupName
            moving.StandardName = target.StandardName
            target.COLUMNS = [...(target.COLUMNS || []), moving]
          }
        }
        syncMasterFromGroups(m)
      })
      if (!targetStdName) setSelection({ kind: 'none' })
    },
    [updateModel]
  )

  const handleColumnFieldChange = useCallback(
    (colId: number, field: keyof VisualColumn, value: unknown) => {
      updateModel((m) => {
        for (const g of m.LAYOUT_DATA.visualLayout.ColumnGroups || []) {
          const col = (g.COLUMNS || []).find((c) => c.COLID === colId)
          if (col) (col as Record<string, unknown>)[field as string] = value
        }
        syncMasterFromGroups(m)
      })
    },
    [updateModel]
  )

  const findGroupForColumn = useCallback(
    (colId: number): string | null => {
      for (const g of model.LAYOUT_DATA.visualLayout.ColumnGroups || []) {
        if ((g.COLUMNS || []).some((c) => c.COLID === colId)) return g.StandardName
      }
      return null
    },
    [model]
  )

  // ── Criteria ──────────────────────────────────────────────────
  const addSchemaCriteria = useCallback(
    (bankKey: string, bank: BankColumn[], groupPath: number[] | null = null) => {
      const src = bank.find((b) => b.key === bankKey)
      if (!src) return
      updateModel((m) => {
        const nodes = m.SQLMODEL.CRITERIA.query.rules
        const leaves = criteriaLeaves(nodes)
        // The SAME column may be dropped more than once — e.g. a range built
        // from two criteria (TRAN_DATE >= <prompt> AND TRAN_DATE < <fixed>).
        // The engine disambiguates the prompt names (unique_prompt_name); we
        // just need a unique rule id (across groups too).
        let n = leaves.length + 1
        while (leaves.some((r) => r.id === `Criteria_${n}`)) n += 1
        const rule = schemaColumnToCriteria(src, n)
        const dupCount = leaves.filter(
          (r) => (r.field || '').toUpperCase() === rule.field.toUpperCase()
        ).length
        // A second drop of the same column gets a distinguishing label so the
        // two prompts don't render with identical headings.
        if (dupCount > 0) rule.promptLabel = `${rule.promptLabel} (${dupCount + 1})`
        // Dropped onto a condition group (at any nesting depth) → the rule
        // joins that group; otherwise it lands top-level.
        const target = groupPath && groupPath.length
          ? criteriaGroupAtPath(nodes, groupPath) : null
        if (target) target.rules.push(rule)
        else nodes.push(rule)
      })
    },
    [updateModel]
  )

  // SQL mode only — its criteria list is always FLAT (derived from the ?.var
  // placeholders), so a plain index addresses the rule.
  const handleCriteriaChange = useCallback(
    (idx: number, field: keyof CriteriaRule, value: unknown) => {
      updateModel((m) => {
        const rule = m.SQLMODEL.CRITERIA.query.rules[idx]
        if (!rule || isCriteriaGroup(rule)) return
        ;(rule as Record<string, unknown>)[field as string] = value
      })
    },
    [updateModel]
  )

  // SQL mode: the prompt's data type + default live in the SQL placeholder
  // itself (insight-bi binding `?.name.<type>.<default>`). Editing them rewrites
  // the SOURCE_SQL, then re-derives the criteria so the card stays in sync.
  const handleSqlBinding = useCallback(
    (varName: string, type: 'string' | 'number' | 'date' | undefined, defaultValue: string | undefined) => {
      updateModel((m) => {
        m.SOURCE_SQL = setSqlBinding(m.SOURCE_SQL || '', varName, type, defaultValue)
        m.SQLMODEL.CRITERIA.query.rules = deriveSqlCriteria(
          m.SOURCE_SQL, m.SQLMODEL.CRITERIA.query.rules,
        ).rules
      })
    },
    [updateModel]
  )


  const handleOptionChange = useCallback(
    (optionId: string, value: string) => {
      updateModel((m) => {
        const opt = (m.LAYOUT_DATA.visualLayout.options || []).find((o) => o.optionId === optionId)
        if (opt) opt.defaultValue = value
      })
    },
    [updateModel]
  )

  // ── Save ──────────────────────────────────────────────────────
  const handleSave = useCallback(async (): Promise<boolean> => {
    if (!projectName || !objectName) return false
    setSaving(true)
    try {
      await saveVisualObject(projectName, objectName, model)
      setSavedSnapshot(JSON.stringify(model))
      toast.success('Visual saved')
      // Tell the embedding shell the object now exists on disk: its Live
      // Preview tab is gated on that, answered once when the node was
      // selected, so a first save would otherwise leave the tab missing
      // until the node was reselected.
      onSaved?.()
      return true
    } catch (err) {
      console.error('Save failed', err)
      toast.error('Failed to save visual')
      return false
    } finally {
      setSaving(false)
    }
  }, [projectName, objectName, model, onSaved])

  useEffect(() => {
    if (!embedded || !onDirtyChange) return
    onDirtyChange(isDirty)
    return () => onDirtyChange(false)
  }, [embedded, isDirty, onDirtyChange])
  useEffect(() => {
    if (!embedded || !registerSave) return
    registerSave(handleSave)
  }, [embedded, registerSave, handleSave])

  const handleBack = useCallback(() => {
    const selectedNodeId = (location.state as { selectedNodeId?: string })?.selectedNodeId
    guard(() => navigate('/workbench', { state: { selectedNodeId } }))
  }, [guard, location.state, navigate])

  const handleSaveAndContinue = useCallback(async () => {
    const ok = await handleSave()
    if (ok) confirmDiscard()
  }, [handleSave, confirmDiscard])

  // ── SQL mode: real result columns, straight from the database ─
  // The SELECT-list parse cannot expand `SELECT *` — it would offer a literal
  // `*` chip. When the SQL uses a star we ask the server what the query
  // actually returns (read-only, one row) and use THAT as the bank. Debounced,
  // because this fires while the author is still typing.
  const sourceSql = model.SOURCE_SQL || ''
  const connectionId = model.SQLMODEL?.DATABASE_DETAIL?.CONNECTION_ID || ''
  const sqlHasStar = useMemo(() => parseSourceSql(sourceSql).hasStar, [sourceSql])
  const [sqlCols, setSqlCols] =
    useState<{ sql: string; columns: ResolvedSqlColumn[] } | null>(null)
  const [sqlColsStatus, setSqlColsStatus] =
    useState<{ busy: boolean; error: string }>({ busy: false, error: '' })
  // The request needs the WHOLE model (connection / business area resolution),
  // but the effect must not re-fire on every unrelated keystroke — read the
  // current model through a ref and depend only on what changes the result.
  const modelRef = useRef(model)
  modelRef.current = model

  // ── Dialect-aware function catalog (Aggregation dropdown, ranking, and
  // function conditions). Resolved server-side from the visual's ACTUAL data
  // source (connection / schema / business area → preview DB), so the picker
  // only offers what the bound database can run (e.g. MEDIAN on Oracle/
  // Dremio but not SQLite; STDEV spelling handled by the engine).
  const schemaNameDep = model.SQLMODEL?.SCHEMA_NAME || ''
  const businessAreaDep = model.SQLMODEL?.BUSINESS_AREA || ''
  const [fnCatalog, setFnCatalog] = useState<VisualFunction[]>(PORTABLE_FUNCTIONS)
  useEffect(() => {
    if (!projectName) return
    let cancelled = false
    fetchVisualFunctions({ project: projectName, model: modelRef.current })
      .then((res) => {
        if (!cancelled && Array.isArray(res.functions) && res.functions.length) {
          setFnCatalog(res.functions)
        }
      })
      .catch(() => { /* keep the portable fallback list */ })
    return () => { cancelled = true }
  }, [projectName, schemaNameDep, businessAreaDep, connectionId])

  useEffect(() => {
    if (mode !== 'sql' || !sqlHasStar || !sourceSql.trim() || !projectName) {
      setSqlCols(null)
      setSqlColsStatus({ busy: false, error: '' })
      return
    }
    let cancelled = false
    setSqlColsStatus({ busy: true, error: '' })
    const timer = setTimeout(() => {
      fetchSqlColumns({ project: projectName, model: modelRef.current })
        .then((res) => {
          if (cancelled) return
          setSqlCols({ sql: sourceSql, columns: res.columns || [] })
          setSqlColsStatus({ busy: false, error: '' })
        })
        .catch((err: unknown) => {
          if (cancelled) return
          const detail = (err as { response?: { data?: { detail?: string } } })
            ?.response?.data?.detail
          setSqlCols(null)
          setSqlColsStatus({
            busy: false,
            error: detail || (err as Error)?.message
              || 'Could not read the query’s columns',
          })
        })
    }, 700)
    return () => { cancelled = true; clearTimeout(timer) }
  }, [mode, sqlHasStar, sourceSql, connectionId, businessArea, projectName])

  // ── Derived column bank + placement state ─────────────────────
  const bank = useMemo(
    () => availableColumns(
      model, schemaData,
      sqlCols && sqlCols.sql === sourceSql ? sqlCols.columns : null,
    ),
    [model, schemaData, sqlCols, sourceSql],
  )

  // Schema mode: option-referenced columns (group-by / trend / breakup / gauge)
  // that aren't placed on a card slot must still be SELECTed so their values
  // reach the renderer. Inject any missing ones into the master column list as
  // hidden columns. (SQL mode already exposes every SELECT alias, so this is a
  // no-op there.) Converges: once injected they're present, so it stops.
  useEffect(() => {
    if ((model.DATA_SOURCE_MODE || 'sql') !== 'sqlmodel') return
    const referenced = new Set<string>()
    for (const o of model.LAYOUT_DATA.visualLayout.options || []) {
      if (o.optionType !== 'column' && o.optionType !== 'column-multi') continue
      for (const nm of (o.defaultValue || '').split(',').map((s) => s.trim()).filter(Boolean)) {
        referenced.add(nm.toUpperCase())
      }
    }
    if (!referenced.size) return
    const master = model.SQLMODEL.SQLModel.COLUMNS?.[0]?.COLUMN || []
    const have = new Set(master.map((c) => (c.NAME || '').toUpperCase()))
    const toAdd = [...referenced]
      .filter((n) => !have.has(n))
      .map((n) => bank.find((b) => (b.name || '').toUpperCase() === n))
      .filter((b): b is BankColumn => !!b)
    if (!toAdd.length) return
    updateModel((m) => {
      const cols = m.SQLMODEL.SQLModel.COLUMNS?.[0]?.COLUMN || []
      let nextId = cols.length ? Math.max(...cols.map((c) => c.COLID || 0), 1) + 1 : 2
      for (const b of toAdd) {
        const col = materializeColumn(b, nextId++)
        ;(col as Record<string, unknown>).HIDDEN = true
        cols.push(col)
      }
      m.SQLMODEL.SQLModel.COLUMNS = [{ COLUMN: cols }]
    })
  }, [model, bank, updateModel])
  const placedKeys = useMemo(() => {
    const s = new Set<string>()
    for (const g of model.LAYOUT_DATA.visualLayout.ColumnGroups || []) {
      for (const c of g.COLUMNS || []) s.add(bankKeyOf(c, mode))
    }
    return s
  }, [model, mode])
  const unplaced = useMemo(
    () => bank.filter((b) => !placedKeys.has(b.key)),
    [bank, placedKeys]
  )

  // ── Render ────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center" style={{ height: '100%' }}>
        <Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} />
        <span style={{ marginLeft: 8 }}>Loading visual…</span>
      </div>
    )
  }

  return (
    <div style={embedded && embedFullscreen ? {
      position: 'fixed', inset: 0, zIndex: 1000, background: 'var(--color-bg)',
      display: 'flex', flexDirection: 'column', padding: '0 12px',
    } : {
      display: 'flex', flexDirection: 'column', height: '100%',
    }}>
      <style>{VDX_CSS}</style>
      {/* Builder header (§8.3.1) — surface bar: V chip, name, meta; actions right */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '9px 12px', flexShrink: 0, gap: 12,
        background: 'var(--color-bg-secondary)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--radius-md)',
        margin: '6px 0 0',
      }}>
        <div className="flex items-center gap-2" style={{ minWidth: 0 }}>
          {!embedded && (
            <button className="btn btn-ghost btn-sm" onClick={handleBack} title="Back to workbench">
              <ArrowLeft size={16} />
            </button>
          )}
          <TypeChip code="V" size={22} />
          <div style={{
            minWidth: 0, color: 'var(--color-text)',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            <span style={{ fontSize: 18, fontWeight: 700, letterSpacing: '-0.3px' }}>
              {objectName}
            </span>
            <span style={{
              marginLeft: 10, fontSize: 14, fontWeight: 400,
              color: 'var(--color-text-faint)',
            }}>
              {projectName} · Visual
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Visual Type lives in the toolbar — it is not data-source info.
              The picker opens a modal of icon + description cards rather than a
              bare list of 28 names. */}
          <label style={{ fontSize: 13, color: 'var(--color-text-faint)' }}>Visual</label>
          <VisualTypePicker
            value={model.SQLMODEL.VISUAL_NAME}
            onChange={handleVisualChange}
          />
          <div style={{ width: 1, height: 20, background: 'var(--color-border)' }} />
          {canEdit && (
            <>
              <UndoRedoButtons history={history} />
              <div style={{ width: 1, height: 20, background: 'var(--color-border)' }} />
            </>
          )}
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => setPreviewOpen(true)}
            title="Preview this visual with live data"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}
          >
            <Eye size={14} /> Preview
          </button>
          {canEdit ? (
            <button
              className={`btn btn-sm ${isDirty ? 'btn-primary' : 'btn-secondary'}`}
              onClick={handleSave}
              disabled={saving}
              title={isDirty ? 'You have unsaved changes' : 'No changes to save'}
              style={isDirty ? { boxShadow: '0 0 0 3px var(--color-accent-weak)' } : undefined}
            >
              {saving ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Save size={14} />}
              {saving ? 'Saving…' : isDirty ? 'Save *' : 'Save'}
            </button>
          ) : (
            <ReadOnlyBadge />
          )}
          {embedded && !hostFullscreen && (
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => setEmbedFullscreen((f) => !f)}
              title={embedFullscreen ? 'Restore' : 'Full screen'}
            >
              {embedFullscreen ? <Minimize2 size={15} /> : <Maximize2 size={15} />}
            </button>
          )}
        </div>
      </div>

      {/* Builder tabs (§8.3.2) — underline style */}
      <div style={{
        display: 'flex', gap: 22, flexShrink: 0,
        borderBottom: '1px solid var(--color-border)',
        padding: '0 6px',
      }}>
        {([
          { key: 'source', label: 'Data Source' },
          { key: 'layout', label: 'Layout' },
          { key: 'options', label: 'Options' },
        ] as const).map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            style={{
              padding: '10px 2px', border: 'none', background: 'transparent',
              borderBottom: tab === t.key ? '2px solid var(--color-accent)' : '2px solid transparent',
              fontSize: 15, fontWeight: tab === t.key ? 600 : 400,
              color: tab === t.key ? 'var(--color-text)' : 'var(--color-text-secondary)',
              cursor: 'pointer',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Body — fixed chrome above; each tab body scrolls on its own */}
      <div style={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden' }}>
        {tab === 'source' && (
          <DataSourceTab
            model={model}
            project={projectName}
            schemaData={schemaData}
            schemaList={schemaList}
            schemaBusy={schemaBusy}
            areaList={areaList}
            areaSchemaName={areaSchemaName}
            bank={bank}
            projectVars={projectVars}
            sqlHasStar={sqlHasStar}
            sqlColsStatus={sqlColsStatus}
            onModeChange={handleModeChange}
            onSourceSqlChange={handleSourceSqlChange}
            onSchemaNameChange={handleSchemaNameChange}
            onBusinessAreaChange={(name) => void handleBusinessAreaChange(name)}
            onEditSchema={() => setEditSchemaOpen(true)}
            onAddSchemaCriteria={(key, groupPath) => addSchemaCriteria(key, bank, groupPath)}
            onCriteriaChange={handleCriteriaChange}
            onSqlBinding={handleSqlBinding}
            onModelChange={updateModel}
            dragItem={dragItem}
            setDragItem={setDragItem}
            fnCatalog={fnCatalog}
          />
        )}

        {tab === 'layout' && (
          <LayoutTab
            model={model}
            fnCatalog={fnCatalog}
            catalogEntry={catalogEntry}
            mode={mode}
            schemaData={schemaData}
            bank={bank}
            unplaced={unplaced}
            selection={selection}
            setSelection={setSelection}
            schemaMissing={mode === 'sqlmodel' && !schemaData}
            sqlMissing={mode === 'sql' && !(model.SOURCE_SQL || '').trim()}
            findGroup={findGroupForColumn}
            onPlaceBank={(key, std) => placeBankColumn(key, std, bank)}
            onMoveColumn={moveColumn}
            onColumnFieldChange={handleColumnFieldChange}
            dragItem={dragItem}
            setDragItem={setDragItem}
            footer={isDocumentVisual ? (
              <DocumentOutputPanel
                project={projectName}
                objName={objectName}
                model={model}
                nonce={documentNonce}
                // The document designer reads the visual from DISK, so an
                // unsaved column would be missing from the panel it is about
                // to lay out. Saving first is what makes "drag a column, then
                // design" work in the order an author does it.
                onSaveFirst={handleSave}
                onOpenDesigner={() => setEditDocumentOpen(true)}
              />
            ) : null}
          />
        )}

        {tab === 'options' && (
          <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '22px 28px 60px', background: 'var(--color-bg)' }}>
            <OptionsTab
              model={model}
              catalogEntry={catalogEntry}
              bank={bank}
              showOverridesOnly={showOverridesOnly}
              setShowOverridesOnly={setShowOverridesOnly}
              onOptionChange={handleOptionChange}
            />
          </div>
        )}
      </div>

      {/* In-place schema editor overlay */}
      {editSchemaOpen && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 1100, background: 'var(--color-bg)',
          display: 'flex', flexDirection: 'column',
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '6px 12px', borderBottom: '1px solid var(--color-border)',
            background: 'var(--color-bg-secondary)',
          }}>
            <div className="flex items-center gap-2" style={{ fontSize: 14, fontWeight: 600 }}>
              <Database size={15} style={{ color: 'var(--color-accent)' }} />
              Editing schema · {schemaName || '(unnamed)'}
            </div>
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => { setEditSchemaOpen(false); setSchemaNonce((n) => n + 1) }}
              title="Close the schema editor and return to the visual"
            >
              <X size={14} /> Done
            </button>
          </div>
          <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <SchemaDesignerPage
              embedded
              embedProject={projectName}
              embedObjName={schemaName}
            />
          </div>
        </div>
      )}

      {/* In-place document designer overlay.
          Same arrangement as the schema editor above, and for the same
          reason: the author is in the middle of one piece of work, and
          navigating away from a half-built visual to lay out its document
          loses the thread — and the unsaved model with it. */}
      {editDocumentOpen && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 1100, background: 'var(--color-bg)',
          display: 'flex', flexDirection: 'column',
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '6px 12px', borderBottom: '1px solid var(--color-border)',
            background: 'var(--color-bg-secondary)',
          }}>
            <div className="flex items-center gap-2" style={{ fontSize: 14, fontWeight: 600 }}>
              <FileText size={15} style={{ color: 'var(--color-accent)' }} />
              Designing the document · {objectName}
            </div>
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => {
                setEditDocumentOpen(false)
                // Re-read the pairing: the identity and the title may have
                // changed inside, and the panel is what reports them.
                setDocumentNonce((n) => n + 1)
              }}
              title="Close the document designer and return to the visual"
            >
              <X size={14} /> Done
            </button>
          </div>
          <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <DocumentFormDesignerPage
              embedded
              embedProject={projectName}
              embedObjName={objectName}
              /* Straight to the page. The source, the identity and the keys
                 were just written by the panel that opened this, so Settings
                 is a tab of answers the author did not ask a question for. */
              initialTab="body"
            />
          </div>
        </div>
      )}

      <DiscardChangesModal
        open={showDiscardModal}
        onCancel={cancelDiscard}
        onDiscard={confirmDiscard}
        onSave={handleSaveAndContinue}
        saving={saving}
      />

      {/* Live preview — the shared standalone modal (a fixed overlay, outside
          this designer's layout) so the same surface serves the card "View"
          and app navigation without a duplicate implementation. */}
      <VisualPreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        projectName={projectName}
        objectName={objectName}
        model={model}
      />
    </div>
  )
}

// ════════════════════════════════════════════════════════════════
// Data Source tab
// ════════════════════════════════════════════════════════════════
function DataSourceTab({
  model, project, schemaData, schemaList, schemaBusy, areaList, areaSchemaName,
  bank, projectVars, sqlHasStar, sqlColsStatus,
  onModeChange, onSourceSqlChange, onSchemaNameChange, onBusinessAreaChange,
  onEditSchema,
  onAddSchemaCriteria, onCriteriaChange, onSqlBinding, onModelChange,
  dragItem, setDragItem, fnCatalog,
}: {
  model: VisualModel
  fnCatalog: VisualFunction[]
  project: string
  schemaData: VisualSchema | null
  schemaList: VisualSchemaSummary[]
  schemaBusy: boolean
  areaList: BusinessAreaSummary[]
  areaSchemaName: string
  bank: BankColumn[]
  projectVars: ProjectVariable[]
  /** The SELECT list uses `*` / `t.*` — columns come from the server. */
  sqlHasStar: boolean
  sqlColsStatus: { busy: boolean; error: string }
  onModeChange: (m: 'sql' | 'sqlmodel') => void
  onSourceSqlChange: (v: string) => void
  onSchemaNameChange: (name: string) => void
  onBusinessAreaChange: (name: string) => void
  onEditSchema: () => void
  onAddSchemaCriteria: (key: string, groupPath?: number[] | null) => void
  onCriteriaChange: (idx: number, field: keyof CriteriaRule, value: unknown) => void
  onSqlBinding: (varName: string, type: 'string' | 'number' | 'date' | undefined, defaultValue: string | undefined) => void
  onModelChange: (mutator: (m: VisualModel) => void) => void
  dragItem: DragItem | null
  setDragItem: (d: DragItem | null) => void
}) {
  const mode = model.DATA_SOURCE_MODE || 'sql'
  const businessArea = (model.SQLMODEL.BUSINESS_AREA || '').trim()
  const nodes = model.SQLMODEL.CRITERIA.query.rules
  // SQL mode's criteria are always FLAT (groups are a schema-mode feature).
  const rules = useMemo(() => criteriaLeaves(nodes), [nodes])
  const { systemTokens } = useMemo(
    () => deriveSqlCriteria(model.SOURCE_SQL || '', nodes),
    [model.SOURCE_SQL, nodes]
  )
  // SQL mode: a criteria card for a JUST-typed ?.placeholder mounts expanded;
  // the rest start collapsed behind their summaries.
  const knownIdsRef = useRef<Set<string> | null>(null)
  const knownIds = knownIdsRef.current
  const freshIds = knownIds
    ? new Set(rules.filter((r) => !knownIds.has(r.id)).map((r) => r.id))
    : new Set<string>()
  useEffect(() => {
    knownIdsRef.current = new Set(rules.map((r) => r.id))
  }, [rules])
  // Expand/collapse-ALL broadcast for the SQL-mode criteria cards.
  const [cardSignal, setCardSignal] = useState<{ open: boolean; seq: number }>({ open: false, seq: 0 })

  // ── Collapsible top sections ──
  // Once a Business Area is picked, sections 1 & 2 minimize to one-line
  // headers (the picked name stays visible in the header); clicking a header
  // re-expands it. Opening the designer with an area already set starts
  // collapsed so the working area (columns + criteria) gets the space.
  const [baOpen, setBaOpen] = useState(!businessArea)
  const [dsOpen, setDsOpen] = useState(!businessArea)
  const prevAreaRef = useRef(businessArea)
  useEffect(() => {
    if (prevAreaRef.current === businessArea) return
    prevAreaRef.current = businessArea
    if (businessArea) { setBaOpen(false); setDsOpen(false) }
    else setBaOpen(true)
  }, [businessArea])

  const modeLabel = mode === 'sqlmodel'
    ? (businessArea
        ? 'Business Area dataset'
        : `Schema${(model.SQLMODEL.SCHEMA_NAME || '').trim() ? ` · ${model.SQLMODEL.SCHEMA_NAME}` : ''}`)
    : 'SQL Query'

  return (
    <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '24px 28px 60px', background: 'var(--color-bg)' }}>
      <div style={{ maxWidth: 1040 }}>

      {/* 1 · Business area — the analytics basis of this visual. 'Default'
          needs no definition: the visual reads the project database / its own
          schema exactly as before. */}
      <SectionToggleHeader
        label="1 · Business area"
        badge={<span style={newBadgeStyle}>New</span>}
        open={baOpen}
        summary={businessArea || 'Default'}
        onToggle={() => setBaOpen((o) => !o)}
      />
      {baOpen && (
        <>
          <div style={helperStyle}>
            Pick the topic this visual belongs to — its tables, folders and security
            come along automatically.
          </div>
          <div style={{ margin: '12px 0 26px' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
              <AreaCard
                name="Default"
                description="No business area — the visual reads the project database / its own schema directly."
                dotColor="var(--color-text-faint)"
                active={!businessArea}
                onPick={() => { if (businessArea) onBusinessAreaChange('') }}
              />
              {areaList.map((a, i) => (
                <AreaCard
                  key={a.area_name}
                  name={a.area_name}
                  description={a.description || 'Business area'}
                  tableCount={a.table_count}
                  dotColor={AREA_DOT_COLORS[i % AREA_DOT_COLORS.length]}
                  active={businessArea === a.area_name}
                  onPick={() => { if (businessArea !== a.area_name) onBusinessAreaChange(a.area_name) }}
                />
              ))}
            </div>
            {businessArea && (
              <div style={hintStyle}>
                Data comes from Business Area <strong>{businessArea}</strong>
                {areaSchemaName
                  ? <> (dataset: schema <strong>{areaSchemaName}</strong>)</>
                  : <> (its own tables &amp; joins)</>}
                {' '}— including its data-source connection and row/column security.
              </div>
            )}
          </div>
        </>
      )}

      {/* 2 · Data source — schema-vs-SQL choice cards */}
      <div style={{ marginTop: baOpen ? 0 : 10 }}>
        <SectionToggleHeader
          label="2 · Data source"
          open={dsOpen}
          summary={modeLabel}
          onToggle={() => setDsOpen((o) => !o)}
        />
      </div>
      {dsOpen && (
        <div style={{ display: 'flex', gap: 12, margin: '12px 0 26px', maxWidth: 720 }}>
          <ModeButton
            active={mode === 'sqlmodel'}
            label={businessArea ? 'Business Area dataset' : 'Schema'}
            hint={businessArea
              ? 'Use the business area’s model — drag its columns to build the layout & filters. Recommended.'
              : 'Use a reusable schema model — drag columns to build the layout & filters. Recommended.'}
            onClick={() => onModeChange('sqlmodel')}
          />
          <ModeButton
            active={mode === 'sql'}
            label="SQL Query"
            hint="Write SQL — columns come from the SELECT, prompts from ?. placeholders."
            onClick={() => onModeChange('sql')}
          />
        </div>
      )}
      {!dsOpen && <div style={{ height: 14 }} />}

      {mode === 'sql' ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.6fr) minmax(300px, 1fr)', gap: 20 }}>
          {/* SQL editor */}
          <div>
            <SectionHeader>SQL query</SectionHeader>
            <textarea
              value={model.SOURCE_SQL || ''}
              onChange={(e) => onSourceSqlChange(e.target.value)}
              spellCheck={false}
              style={{
                width: '100%', minHeight: 320, fontFamily: MONO_FONT, fontSize: 14.5,
                padding: '12px 14px', background: 'var(--color-input-bg)', color: 'var(--color-text)',
                border: '1px solid var(--color-border)', borderRadius: 11,
                resize: 'vertical', lineHeight: 1.6,
              }}
              placeholder="SELECT t.region AS region, SUM(t.value) AS total&#10;FROM sales t&#10;WHERE t.txn_date BETWEEN ?.from_date AND ?.to_date&#10;  AND t.site = ?.@LoginSite&#10;GROUP BY t.region"
            />
            <div style={hintStyle}>
              Columns come from the <code>SELECT</code> aliases (place them on the
              chart in the <strong>Layout</strong> tab). Use <code>?.name</code> for a
              user prompt and <code>?.@Token</code> for system inputs (LoginEmployee,
              Today, 1stDayOfMonth…).
            </div>
            {/* `SELECT *` names no columns — the database is asked what the
                query really returns, so the bank shows real columns, not `*`. */}
            {sqlHasStar && (
              <div style={{
                ...hintStyle,
                color: sqlColsStatus.error ? 'var(--color-error)' : undefined,
              }}>
                {sqlColsStatus.busy
                  ? 'Reading the query’s columns from the database…'
                  : sqlColsStatus.error
                  ? `Could not expand ${'*'} — ${sqlColsStatus.error}`
                  : bank.length
                  ? `${'*'} expanded to ${bank.length} column${bank.length === 1 ? '' : 's'} from the database.`
                  : `${'*'} will be expanded from the database once the query runs.`}
              </div>
            )}
          </div>

          {/* Auto-derived criteria */}
          <div>
            <SectionHeader
              actions={rules.length > 0 && (
                <FolderExpandControls
                  expandTitle="Expand all criteria"
                  collapseTitle="Collapse all criteria"
                  onExpandAll={() => setCardSignal((s) => ({ open: true, seq: s.seq + 1 }))}
                  onCollapseAll={() => setCardSignal((s) => ({ open: false, seq: s.seq + 1 }))}
                />
              )}
            >
              Criteria ({rules.length})
              <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0, marginLeft: 6 }}>
                · auto from <code>?.</code> prompts
              </span>
            </SectionHeader>
            {rules.length === 0 && (
              <div style={emptyHintStyle}>
                Add a <code>?.placeholder</code> to the SQL — a criterion appears here
                automatically.
              </div>
            )}
            {rules.map((rule, i) => (
              <CriteriaCard
                key={rule.id || i}
                rule={rule}
                idx={i}
                project={project}
                allRules={rules}
                fieldLocked
                sqlMode
                projectVars={projectVars}
                defaultOpen={freshIds.has(rule.id)}
                expandSignal={cardSignal}
                onChange={onCriteriaChange}
                onSqlBinding={onSqlBinding}
              />
            ))}
            {systemTokens.length > 0 && (
              <div style={{ marginTop: 10 }}>
                <div style={{ ...hintStyle, marginBottom: 4 }}>System inputs (resolved automatically):</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {systemTokens.map((t) => (
                    <span key={t} style={tokenChipStyle}>?.@{t}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <SchemaSource
          model={model}
          project={project}
          schemaData={schemaData}
          schemaList={schemaList}
          schemaBusy={schemaBusy}
          businessArea={businessArea}
          areaSchemaName={areaSchemaName}
          bank={bank}
          nodes={nodes}
          projectVars={projectVars}
          onSchemaNameChange={onSchemaNameChange}
          onEditSchema={onEditSchema}
          onAddSchemaCriteria={onAddSchemaCriteria}
          onModelChange={onModelChange}
          dragItem={dragItem}
          setDragItem={setDragItem}
          fnCatalog={fnCatalog}
        />
      )}
      </div>
    </div>
  )
}

// ── Schema-mode data source: picker + tree + criteria builder ─────
function SchemaSource({
  model, project, schemaData, schemaList, schemaBusy, businessArea,
  areaSchemaName, bank, nodes, projectVars,
  onSchemaNameChange, onEditSchema, onAddSchemaCriteria,
  onModelChange, dragItem, setDragItem, fnCatalog,
}: {
  model: VisualModel
  fnCatalog: VisualFunction[]
  project: string
  schemaData: VisualSchema | null
  schemaList: VisualSchemaSummary[]
  schemaBusy: boolean
  businessArea: string
  areaSchemaName: string
  bank: BankColumn[]
  nodes: CriteriaNode[]
  projectVars: ProjectVariable[]
  onSchemaNameChange: (name: string) => void
  onEditSchema: () => void
  onAddSchemaCriteria: (key: string, groupPath?: number[] | null) => void
  onModelChange: (mutator: (m: VisualModel) => void) => void
  dragItem: DragItem | null
  setDragItem: (d: DragItem | null) => void
}) {
  const schemaName = model.SQLMODEL.SCHEMA_NAME || ''
  const tables = useMemo(() => groupByTable(bank), [bank])
  const [dropActive, setDropActive] = useState(false)
  // Which condition GROUP the current drag is hovering (its own drop zone),
  // as the group's index-path key, e.g. '2' or '2.0' for a nested group.
  const [groupDrop, setGroupDrop] = useState<string | null>(null)
  const leaves = useMemo(() => criteriaLeaves(nodes), [nodes])
  // Freshly-dropped criteria mount EXPANDED; existing ones start collapsed
  // (the collapsed header keeps a one-line summary visible).
  const knownIdsRef = useRef<Set<string> | null>(null)
  const knownIds = knownIdsRef.current
  const freshIds = knownIds
    ? new Set(leaves.filter((r) => !knownIds.has(r.id)).map((r) => r.id))
    : new Set<string>()
  useEffect(() => {
    knownIdsRef.current = new Set(leaves.map((r) => r.id))
  }, [leaves])

  // ── Criteria mutations — addressed by GROUP PATH (index path from the
  // top-level rules to a group, [] = top level) + an index inside that
  // group's list. Groups nest to any depth. ──
  const listAt = (m: VisualModel, groupPath: number[]): CriteriaNode[] | null => {
    const rules = m.SQLMODEL.CRITERIA.query.rules
    if (!groupPath.length) return rules
    const group = criteriaGroupAtPath(rules, groupPath)
    return group ? group.rules : null
  }
  const changeAt = (groupPath: number[], idx: number) =>
    (_i: number, field: keyof CriteriaRule, value: unknown) =>
      onModelChange((m) => {
        const node = listAt(m, groupPath)?.[idx]
        if (node && !isCriteriaGroup(node)) (node as Record<string, unknown>)[field as string] = value
      })
  const deleteAt = (groupPath: number[], idx: number) => () =>
    onModelChange((m) => {
      listAt(m, groupPath)?.splice(idx, 1)
    })
  // Add a condition group — at the top level or nested inside an existing
  // group. New groups default to OR (the typical use is `(a OR b)`
  // alternatives AND-ed with the rest) and get a unique designer-only id
  // (collapse-state key; engines ignore it).
  const addGroupAt = (groupPath: number[]) =>
    onModelChange((m) => {
      const list = listAt(m, groupPath)
      if (!list) return
      const ids = new Set<string>()
      const collect = (ns: CriteriaNode[]) => ns.forEach((n) => {
        if (isCriteriaGroup(n)) { if (typeof n.id === 'string') ids.add(n.id); collect(n.rules || []) }
      })
      collect(m.SQLMODEL.CRITERIA.query.rules)
      let g = 1
      while (ids.has(`Group_${g}`)) g += 1
      list.push({ condition: 'or', rules: [], id: `Group_${g}` })
    })
  const setGroupCondition = (groupPath: number[], cond: 'and' | 'or') =>
    onModelChange((m) => {
      const group = criteriaGroupAtPath(m.SQLMODEL.CRITERIA.query.rules, groupPath)
      if (group) group.condition = cond
    })
  const ungroupAt = (groupPath: number[]) =>
    onModelChange((m) => {
      const parentList = listAt(m, groupPath.slice(0, -1))
      const idx = groupPath[groupPath.length - 1]
      const node = parentList?.[idx]
      if (parentList && node && isCriteriaGroup(node)) parentList.splice(idx, 1, ...node.rules)
    })
  // ── Group collapse state (keyed by the group's designer id; default open)
  // + the expand/collapse-ALL signal the criteria cards listen to. ──
  const [groupOpen, setGroupOpen] = useState<Record<string, boolean>>({})
  const [cardSignal, setCardSignal] = useState<{ open: boolean; seq: number }>({ open: false, seq: 0 })
  const groupKey = (g: CriteriaGroup, path: number[]) => g.id || `path:${path.join('.')}`
  const isGroupOpen = (g: CriteriaGroup, path: number[]) => groupOpen[groupKey(g, path)] ?? true
  const setAllCriteriaOpen = (open: boolean) => {
    const next: Record<string, boolean> = {}
    const walk = (ns: CriteriaNode[], path: number[]) => ns.forEach((n, i) => {
      if (isCriteriaGroup(n)) { next[groupKey(n, [...path, i])] = open; walk(n.rules || [], [...path, i]) }
    })
    walk(nodes, [])
    setGroupOpen(next)
    setCardSignal((s) => ({ open, seq: s.seq + 1 }))
  }
  // Folder open state — by default every folder is COLLAPSED except the
  // first, so long schemas open compact. Header buttons expand/collapse all.
  const [folderOverrides, setFolderOverrides] = useState<Record<string, boolean>>({})
  const isFolderOpen = (name: string, idx: number) => folderOverrides[name] ?? idx === 0
  const setAllFolders = (open: boolean) => {
    const next: Record<string, boolean> = {}
    for (const t of tables) next[t.table] = open
    setFolderOverrides(next)
  }
  // Enforced per-table filters defined on the schema — surfaced here so the
  // author knows the query will carry them automatically (§8.3.3).
  const condTables = useMemo(
    () => (schemaData?.TABLES || []).filter((t) => (t.CONDITION || '').trim()),
    [schemaData]
  )

  // ── Recursive criteria renderers — a list of nodes is a responsive card
  // grid; a GROUP is a full-width collapsible box (own drop zone + and/or)
  // whose rules render with the same grid, so groups nest to any depth. ──
  function renderGroupBox(group: CriteriaGroup, path: number[], depth: number) {
    const pathKey = path.join('.')
    const cond = (group.condition || 'or').toLowerCase() === 'and' ? 'and' : 'or'
    const open = isGroupOpen(group, path)
    const leafCount = criteriaLeaves(group.rules || []).length
    return (
      <div
        key={group.id || `group-${pathKey}`}
        onDragOver={(e) => {
          if (dragItem?.kind === 'bank') {
            e.preventDefault(); e.stopPropagation(); setGroupDrop(pathKey)
          }
        }}
        onDragLeave={() => setGroupDrop((g) => (g === pathKey ? null : g))}
        onDrop={(e) => {
          e.preventDefault(); e.stopPropagation()
          setGroupDrop(null); setDropActive(false)
          if (dragItem?.kind === 'bank') {
            onAddSchemaCriteria(dragItem.key, path)
            // The fresh card mounts expanded — make sure its group is open too.
            setGroupOpen((prev) => ({ ...prev, [groupKey(group, path)]: true }))
          }
          setDragItem(null)
        }}
        style={{
          gridColumn: '1 / -1',
          border: `1.5px ${groupDrop === pathKey ? 'dashed var(--color-accent)' : 'solid var(--color-border-strong)'}`,
          borderRadius: 10, padding: 10,
          background: groupDrop === pathKey
            ? 'color-mix(in srgb, var(--color-accent) 10%, transparent)'
            : depth % 2 ? 'var(--color-bg-secondary)' : 'var(--color-bg)',
        }}
      >
        {/* Header — click to collapse/expand; the controls stay clickable. */}
        <div
          onClick={() => setGroupOpen((prev) => ({ ...prev, [groupKey(group, path)]: !open }))}
          title={open ? 'Collapse this group — a one-line summary stays visible' : 'Expand this group'}
          style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: open ? 8 : 0, flexWrap: 'wrap', cursor: 'pointer' }}
        >
          {open
            ? <ChevronDown size={13} style={{ flexShrink: 0, color: 'var(--color-text-secondary)' }} />
            : <ChevronRight size={13} style={{ flexShrink: 0, color: 'var(--color-text-secondary)' }} />}
          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: 0.4 }}>
            Condition group
          </span>
          {open ? (
            <>
              <select
                value={cond}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => setGroupCondition(path, e.target.value as 'and' | 'or')}
                title="How the conditions INSIDE this group combine"
                style={{ ...selectStyle, width: 'auto', padding: '3px 6px', fontSize: 13.5 }}
              >
                <option value="or">match ANY (OR)</option>
                <option value="and">match ALL (AND)</option>
              </select>
              <span style={{ fontSize: 13, color: 'var(--color-text-faint)' }}>
                renders as ( … {cond.toUpperCase()} … )
              </span>
            </>
          ) : (
            <span style={{
              flex: 1, minWidth: 0, fontSize: 13, color: 'var(--color-text)',
              padding: '1px 8px', borderRadius: 6,
              background: 'var(--color-surface-2)', border: '1px solid var(--color-border)',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {leafCount} filter{leafCount === 1 ? '' : 's'} · {cond.toUpperCase()}
            </span>
          )}
          {open && <span style={{ flex: 1 }} />}
          {open && (
            <button
              className="btn btn-ghost btn-sm"
              style={{ fontSize: 13 }}
              onClick={(e) => { e.stopPropagation(); addGroupAt(path) }}
              title="Add a nested condition group inside this one, e.g. (a OR (b AND c))"
            >
              <Plus size={12} /> Sub-group
            </button>
          )}
          <button
            className="btn btn-ghost btn-sm"
            style={{ fontSize: 13, ...(open ? {} : { marginLeft: 'auto' }) }}
            onClick={(e) => { e.stopPropagation(); ungroupAt(path) }}
            title="Dissolve the group — its contents move up one level (nothing is deleted)"
          >
            Ungroup
          </button>
        </div>
        {open && (group.rules || []).length === 0 && (
          <div style={emptyHintStyle}>
            Drag a column here — conditions inside this box combine with {cond.toUpperCase()}.
          </div>
        )}
        {open && renderNodes(group.rules || [], path, depth + 1)}
      </div>
    )
  }
  function renderNodes(list: CriteriaNode[], groupPath: number[], depth: number): React.ReactNode {
    return (
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: 10,
      }}>
        {list.map((node, i) => {
          if (isCriteriaGroup(node)) return renderGroupBox(node, [...groupPath, i], depth)
          const rule = node
          if (rule.applyTo === 'row_limit') {
            // Managed by the Sort & Top N panel below — show a read-only chip
            // here so the rule is visible (it renders as a prompt at runtime)
            // without offering column/operator edits that don't apply to it.
            return (
              <div
                key={rule.id || `${groupPath.join('.')}-${i}`}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '8px 10px', borderRadius: 9, fontSize: 13.5,
                  border: '1px dashed var(--color-border-strong)',
                  background: 'var(--color-surface-2)',
                  color: 'var(--color-text-secondary)',
                }}
              >
                <span style={{ fontWeight: 600 }}>
                  {rule.promptLabel || 'Top N'} prompt
                </span>
                <span style={{ color: 'var(--color-text-faint)' }}>
                  row limit · default {rule.value || '—'} · edit in “Sort &amp; Top N” below
                </span>
                <span style={{ flex: 1 }} />
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={deleteAt(groupPath, i)}
                  title="Remove the Top N prompt (no row limit)"
                >
                  <X size={13} />
                </button>
              </div>
            )
          }
          if (rule.applyTo === 'having' || (rule.FUNCTION || '').trim()) {
            // Function condition (HAVING): dialect-supported function over a
            // dropped column or a free-hand expression.
            return (
              <FunctionCriteriaCard
                key={rule.id || `${groupPath.join('.')}-${i}`}
                rule={rule}
                functions={fnCatalog.filter((f) => f.kind !== 'window')}
                bank={bank}
                dragItem={dragItem}
                onChange={(field, value) => changeAt(groupPath, i)(0, field, value)}
                onDelete={deleteAt(groupPath, i)}
              />
            )
          }
          return (
            <CriteriaCard
              key={rule.id || `${groupPath.join('.')}-${i}`}
              rule={rule}
              idx={leaves.indexOf(rule)}
              project={project}
              allRules={leaves}
              projectVars={projectVars}
              defaultOpen={freshIds.has(rule.id)}
              expandSignal={cardSignal}
              onChange={changeAt(groupPath, i)}
              onDelete={deleteAt(groupPath, i)}
            />
          )
        })}
      </div>
    )
  }

  return (
    <div>
      {businessArea ? (
        /* Dataset fixed by the Business Area — no schema picking needed. */
        <div style={hintStyle}>
          Dataset from Business Area <strong>{businessArea}</strong>
          {areaSchemaName && <> via schema <strong>{areaSchemaName}</strong></>}
          . Change it in the Business Area designer, or switch &quot;Based
          on&quot; back to Default to pick a schema directly.
        </div>
      ) : (
        <>
          {/* Schema picker row */}
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
            <div>
              <div style={fieldLabelStyle}>Schema</div>
              <input
                list="visual-schema-list"
                value={schemaName}
                onChange={(e) => onSchemaNameChange(e.target.value)}
                placeholder="Select or name a schema"
                style={{ ...inputStyle, width: 280 }}
              />
              <datalist id="visual-schema-list">
                {schemaList.map((s) => (
                  <option key={s.schema_name} value={s.schema_name}>
                    {s.schema_descr ? `${s.schema_descr} · ${s.table_count} tables` : `${s.table_count} tables`}
                  </option>
                ))}
              </datalist>
            </div>
            <button
              className="btn btn-secondary btn-sm"
              onClick={onEditSchema}
              disabled={!schemaName}
              title={schemaName ? 'Open the schema designer' : 'Name a schema first'}
            >
              <Pencil size={13} /> Edit schema design
            </button>
          </div>
          <div style={hintStyle}>
            A schema is a reusable dataset (tables + joins). Multiple visuals can share
            one. Drag its columns into <strong>Criteria</strong> below to build filters, or
            onto the chart in the <strong>Layout</strong> tab.
          </div>
        </>
      )}

      {/* Filters injected automatically from the Schema Design (§8.3.3) */}
      {condTables.length > 0 && (
        <div style={{
          maxWidth: 720, marginTop: 16, padding: '12px 15px', borderRadius: 11,
          background: 'var(--color-warning-weak)', border: '1px solid var(--color-warning)',
        }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--color-warning)', marginBottom: 6 }}>
            Filters injected automatically from the Schema Design
          </div>
          {condTables.map((t) => (
            <div
              key={t.TABLE_NAME}
              style={{ fontFamily: MONO_FONT, fontSize: 14, color: 'var(--color-text-secondary)', padding: '1.5px 0' }}
            >
              {t.TABLE_NAME} · {t.CONDITION}
            </div>
          ))}
          <div style={{ fontSize: 14, color: 'var(--color-text-faint)', marginTop: 6, lineHeight: 1.5 }}>
            Whenever a column from these tables is used, its condition is added to the query for you.
          </div>
        </div>
      )}

      {/* Tree + criteria builder. Each pane owns its scroll (bounded height)
          so scrolling the column list never moves the criteria drop area —
          drag & drop works with both lists at any scroll position. */}
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(260px, 1fr) minmax(320px, 1.4fr)', gap: 20, marginTop: 20, alignItems: 'start' }}>
        {/* Schema column tree */}
        <div style={{ minWidth: 0 }}>
          <div style={{ ...eyebrowStyle, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginTop: 4, marginBottom: 8 }}>
            <span>Schema columns</span>
            {tables.length > 1 && (
              <FolderExpandControls
                onExpandAll={() => setAllFolders(true)}
                onCollapseAll={() => setAllFolders(false)}
              />
            )}
          </div>
          <div style={{
            ...panelStyle, padding: 8, minHeight: 140,
            maxHeight: 'min(62vh, 560px)', overflowY: 'auto',
          }}>
            {schemaBusy && <div style={emptyHintStyle}><Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} /> Loading schema…</div>}
            {!schemaBusy && !schemaName && (
              <div style={emptyHintStyle}>Pick or name a schema above to see its columns.</div>
            )}
            {!schemaBusy && schemaName && !schemaData && (
              <div style={emptyHintStyle}>
                Schema <strong>{schemaName}</strong> has no tables yet.
                Click <em>Edit schema design</em> to define tables & columns.
              </div>
            )}
            {!schemaBusy && schemaData && tables.length === 0 && (
              <div style={emptyHintStyle}>No columns defined in this schema.</div>
            )}
            {tables.map((t, i) => (
              <SchemaTreeTable
                key={t.table}
                table={t.table}
                columns={t.columns}
                open={isFolderOpen(t.table, i)}
                onToggle={() => setFolderOverrides((prev) => ({
                  ...prev, [t.table]: !isFolderOpen(t.table, i),
                }))}
                setDragItem={setDragItem}
              />
            ))}
          </div>
        </div>

        {/* Criteria drop-zone — own scroll; cards flow in a responsive
            multi-column grid (mirrors the live-preview criteria bar).
            Condition GROUPS render as full-width collapsible boxes with their
            own drop zone + and/or, and nest to any depth, so e.g.
            `(site = x OR (region = y AND tier = 1)) AND date > ?`
            is authored visually. */}
        <div style={{ minWidth: 0 }}>
          <div style={{ ...eyebrowStyle, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginTop: 4, marginBottom: 8 }}>
            <span className="flex items-center gap-1"><Filter size={12} /> Criteria ({leaves.length})</span>
            {(leaves.length > 0 || nodes.length > 0) && (
              <FolderExpandControls
                expandTitle="Expand all criteria"
                collapseTitle="Collapse all criteria"
                onExpandAll={() => setAllCriteriaOpen(true)}
                onCollapseAll={() => setAllCriteriaOpen(false)}
              />
            )}
          </div>
          <div
            onDragOver={(e) => { if (dragItem?.kind === 'bank') { e.preventDefault(); setDropActive(true) } }}
            onDragLeave={() => setDropActive(false)}
            onDrop={(e) => {
              e.preventDefault(); setDropActive(false)
              if (dragItem?.kind === 'bank') onAddSchemaCriteria(dragItem.key)
              setDragItem(null)
            }}
            style={{
              border: `1.5px dashed ${dropActive && groupDrop == null ? 'var(--color-accent)' : 'var(--color-border)'}`,
              borderRadius: 11, padding: 10, minHeight: 140,
              maxHeight: 'min(62vh, 560px)', overflowY: 'auto',
              background: dropActive && groupDrop == null
                ? 'color-mix(in srgb, var(--color-accent) 10%, transparent)'
                : 'var(--color-bg-secondary)',
            }}
          >
            {nodes.length === 0 && (
              <div style={emptyHintStyle}>Drag a column here to add a filter — compare it with a user prompt, a fixed value, or a system variable. Drop the same column twice to build a range (e.g. from/to dates).</div>
            )}
            {renderNodes(nodes, [], 0)}
          </div>
          <div style={{ marginTop: 12, display: 'flex', alignItems: 'flex-end', gap: 14, flexWrap: 'wrap' }}>
            <Field label="Combine filters & groups with">
              <select
                value={model.SQLMODEL.CRITERIA.query.condition || 'and'}
                onChange={(e) => onModelChange((m) => { m.SQLMODEL.CRITERIA.query.condition = e.target.value as 'and' | 'or' })}
                style={{ ...selectStyle, width: 120 }}
              >
                <option value="and">AND</option>
                <option value="or">OR</option>
              </select>
            </Field>
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => addGroupAt([])}
              title="Add a condition group — filters dropped inside combine with their own AND/OR, e.g. (site = x OR site IS NULL). Groups can hold sub-groups for deeper nesting."
              style={{ marginBottom: 10 }}
            >
              <Plus size={13} /> Add condition group
            </button>
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => onModelChange((m) => {
                const list = m.SQLMODEL.CRITERIA.query.rules
                const used = criteriaLeaves(list)
                let n = 1
                while (used.some((r) => r.id === `Criteria_${n}`)) n += 1
                list.push({
                  id: `Criteria_${n}`, field: '', COLTYPE: 'number',
                  currentFieldType: 'number', operator: '>', queryOption: 'F',
                  prevOption: 'F', customValue: 'FixedValue', value: '',
                  chipValue: [], FUNCTION: 'SUM', applyTo: 'having',
                } as CriteriaRule)
              })}
              title="Add a function condition — a database function (SUM, COUNT DISTINCT, MEDIAN, …, as supported by the bound database) over a dropped column or a free-hand expression, applied AFTER aggregation (HAVING)."
              style={{ marginBottom: 10 }}
            >
              <Plus size={13} /> Add function condition
            </button>
          </div>
        </div>
      </div>

      {/* Sort & Top N — ORDER_BY / ROW_LIMIT / the Top-N prompt rule. */}
      <SortTopNPanel model={model} onModelChange={onModelChange} />

      <div style={{ ...hintStyle, marginTop: 18 }}>
        Next: open the <strong>Layout</strong> tab and drag columns onto the chart.
      </div>
    </div>
  )
}

function SchemaTreeTable({
  table, columns, open, onToggle, setDragItem,
}: {
  table: string
  columns: BankColumn[]
  open: boolean
  onToggle: () => void
  setDragItem: (d: DragItem | null) => void
}) {
  return (
    <div style={{ marginBottom: 4 }}>
      <button
        onClick={onToggle}
        style={{
          display: 'flex', alignItems: 'center', gap: 4, width: '100%', border: 'none',
          background: 'transparent', cursor: 'pointer', padding: '3px 2px',
          fontSize: 13, fontWeight: 600, color: 'var(--color-text)',
        }}
      >
        {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
        <Table2 size={12} style={{ opacity: 0.7 }} />
        {table}
        <span style={{ fontWeight: 400, fontSize: 13, color: 'var(--color-text-secondary)' }}>
          ({columns.length})
        </span>
      </button>
      {open && (
        <div style={{ paddingLeft: 16 }}>
          {columns.map((c) => (
            <div
              key={c.key}
              className="vdx-bankchip"
              draggable
              onDragStart={(e) => { e.dataTransfer.effectAllowed = 'copy'; e.dataTransfer.setData('text/plain', c.key); setDragItem({ kind: 'bank', key: c.key }) }}
              onDragEnd={() => setDragItem(null)}
              style={{ background: 'var(--color-bg)' }}
              title={`${c.DBTABLE}.${c.DBNAME} · ${c.type}`}
            >
              <GripVertical size={11} style={{ opacity: 0.5, flexShrink: 0 }} />
              <span style={{ flex: 1, fontSize: 14.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {c.label}
              </span>
              <TypeBadge type={c.type} />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ════════════════════════════════════════════════════════════════
// Layout tab
// ════════════════════════════════════════════════════════════════
function LayoutTab({
  model, catalogEntry, mode, schemaData, bank, unplaced, selection, setSelection,
  schemaMissing, sqlMissing, findGroup,
  onPlaceBank, onMoveColumn, onColumnFieldChange, dragItem, setDragItem,
  fnCatalog, footer,
}: {
  model: VisualModel
  fnCatalog: VisualFunction[]
  catalogEntry: VisualCatalogEntry
  mode: string
  schemaData: VisualSchema | null
  bank: BankColumn[]
  unplaced: BankColumn[]
  selection: Selection
  setSelection: (s: Selection) => void
  schemaMissing: boolean
  sqlMissing: boolean
  findGroup: (colId: number) => string | null
  onPlaceBank: (key: string, std: string) => void
  onMoveColumn: (colId: number, std: string | null) => void
  onColumnFieldChange: (colId: number, field: keyof VisualColumn, value: unknown) => void
  dragItem: DragItem | null
  setDragItem: (d: DragItem | null) => void
  /**
   * Rendered under the drop zones. A Document visual puts its own panel here
   * (what identifies one document, and the way into the document designer);
   * every other visual passes nothing, so the tab is unchanged for them.
   */
  footer?: React.ReactNode
}) {
  const allowDrop = (e: React.DragEvent) => { if (dragItem) e.preventDefault() }
  // Source folders in the Available-columns bank (schema mode). Same default
  // as the Data Source tree: every folder COLLAPSED except the first; the
  // header buttons expand/collapse all. Keyed by folder/table name.
  const bankGroups = useMemo(
    () => (mode === 'sqlmodel' ? groupByTable(unplaced) : [{ table: '', columns: unplaced }]),
    [mode, unplaced]
  )
  const [folderOverrides, setFolderOverrides] = useState<Record<string, boolean>>({})
  const isFolderOpen = (name: string, idx: number) =>
    !name || (folderOverrides[name] ?? idx === 0)
  const toggleFolder = (name: string, idx: number) =>
    setFolderOverrides((prev) => ({ ...prev, [name]: !isFolderOpen(name, idx) }))
  const setAllFolders = (open: boolean) => {
    const next: Record<string, boolean> = {}
    for (const g of bankGroups) if (g.table) next[g.table] = open
    setFolderOverrides(next)
  }
  // Schema tables with an enforced CONDITION — shown as a warn strip under the
  // zones so the author knows those filters ride along automatically.
  const condTables = useMemo(
    () => (mode === 'sqlmodel' ? (schemaData?.TABLES || []).filter((t) => (t.CONDITION || '').trim()) : []),
    [mode, schemaData]
  )

  const onDropOnGroup = (e: React.DragEvent, stdName: string) => {
    e.preventDefault()
    if (dragItem?.kind === 'bank') onPlaceBank(dragItem.key, stdName)
    else if (dragItem?.kind === 'col') onMoveColumn(dragItem.colId, stdName)
    setDragItem(null)
  }
  const onDropOnBank = (e: React.DragEvent) => {
    e.preventDefault()
    if (dragItem?.kind === 'col') onMoveColumn(dragItem.colId, null)
    setDragItem(null)
  }

  return (
    <>
      {/* Left: available columns bank (§8.3.4) */}
      <div style={{
        width: 280, minWidth: 240, flexShrink: 0, minHeight: 0, overflowY: 'auto',
        borderRight: '1px solid var(--color-border)', padding: '16px 14px 40px', background: 'var(--color-bg)',
      }}>
        <div style={{ ...eyebrowStyle, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginTop: 4, marginBottom: 8 }}>
          <span>Available columns ({unplaced.length})</span>
          {mode === 'sqlmodel' && bankGroups.length > 1 && (
            <FolderExpandControls
              onExpandAll={() => setAllFolders(true)}
              onCollapseAll={() => setAllFolders(false)}
            />
          )}
        </div>
        <div
          onDragOver={allowDrop}
          onDrop={onDropOnBank}
          style={{ ...dropTargetStyle, minHeight: 80, padding: 6 }}
        >
          {(schemaMissing || sqlMissing) && (
            <div style={emptyHintStyle}>
              {sqlMissing
                ? 'Write a SQL query in the Data Source tab — its SELECT columns appear here.'
                : 'Pick a schema in the Data Source tab — its columns appear here.'}
            </div>
          )}
          {!schemaMissing && !sqlMissing && bank.length === 0 && (
            <div style={emptyHintStyle}>No columns found in the data source.</div>
          )}
          {!schemaMissing && !sqlMissing && bank.length > 0 && unplaced.length === 0 && (
            <div style={emptyHintStyle}>All columns are placed. Drop here to remove one from the chart.</div>
          )}
          {bankGroups.map((grp, gi) => (
            <div key={grp.table || '_flat'}>
              {grp.table && (
                <div
                  onClick={() => toggleFolder(grp.table, gi)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer',
                    fontSize: 13, fontWeight: 600, color: 'var(--color-text-secondary)',
                    padding: '9px 4px 4px', userSelect: 'none',
                  }}
                  title={isFolderOpen(grp.table, gi) ? 'Collapse folder' : 'Expand folder'}
                >
                  {isFolderOpen(grp.table, gi)
                    ? <ChevronDown size={13} style={{ flexShrink: 0 }} />
                    : <ChevronRight size={13} style={{ flexShrink: 0 }} />}
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {grp.table}
                  </span>
                  <span style={{ fontWeight: 500, color: 'var(--color-text-faint)' }}>
                    {grp.columns.length}
                  </span>
                </div>
              )}
              {isFolderOpen(grp.table, gi) && grp.columns.map((c) => (
                <div
                  key={c.key}
                  className="vdx-bankchip"
                  draggable
                  onDragStart={(e) => { e.dataTransfer.effectAllowed = 'copy'; e.dataTransfer.setData('text/plain', c.key); setDragItem({ kind: 'bank', key: c.key }) }}
                  onDragEnd={() => setDragItem(null)}
                  title={c.DBTABLE ? `${c.DBTABLE}.${c.DBNAME}` : c.name}
                >
                  <GripVertical size={12} style={{ opacity: 0.5, flexShrink: 0 }} />
                  <span style={{ flex: 1, fontSize: 14.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {c.label}
                  </span>
                  {c.isAggregate ? <AggBadge fn="SUM" /> : <TypeBadge type={c.type} />}
                </div>
              ))}
            </div>
          ))}
        </div>
        <div style={{ ...hintStyle, marginTop: 8 }}>
          Drag a column onto a group on the right to put it on the chart.
        </div>
      </div>

      {/* Center: drop-zone boxes (§8.3.4 anatomy) */}
      <div style={{ flex: 1, minWidth: 0, minHeight: 0, overflowY: 'auto', padding: '16px 22px 40px', background: 'var(--color-bg)' }}>
        <SectionHeader>Layout · {catalogEntry.VisualName}</SectionHeader>
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
          gap: 14, marginTop: 10,
        }}>
          {(model.LAYOUT_DATA.visualLayout.ColumnGroups || []).map((g) => (
            <div
              key={g.GroupID}
              onDragOver={allowDrop}
              onDrop={(e) => onDropOnGroup(e, g.StandardName)}
              style={{ ...dropTargetStyle, padding: '11px 12px', minHeight: 120 }}
            >
              <div style={groupHeaderStyle}>
                <span style={{ fontWeight: 700, fontSize: 14 }}>{g.GroupName}</span>
                <span style={{
                  fontSize: 13, fontWeight: 600, color: 'var(--color-text-faint)',
                  padding: '1px 8px', borderRadius: 6,
                  background: 'var(--color-surface-2)', border: '1px solid var(--color-border)',
                  whiteSpace: 'nowrap',
                }}>
                  {g.AllowedColumnTypes}
                  {g.MaxColumns && g.MaxColumns !== 0 ? ` · max ${g.MaxColumns}` : ''}
                </span>
              </div>
              {(g.COLUMNS || []).length === 0 && (
                <div style={emptyHintStyle}>Drop columns here</div>
              )}
              {(g.COLUMNS || []).map((col) => (
                <ColumnChip
                  key={col.COLID}
                  col={col}
                  selected={selection.kind === 'column' && selection.colId === col.COLID}
                  onSelect={() => setSelection({ kind: 'column', colId: col.COLID })}
                  onRemove={() => onMoveColumn(col.COLID, null)}
                  onDragStart={() => setDragItem({ kind: 'col', colId: col.COLID })}
                  onDragEnd={() => setDragItem(null)}
                />
              ))}
            </div>
          ))}
        </div>

        {footer}

        {/* Injected schema-condition filters (warn styling, §8.3.4) */}
        {condTables.length > 0 && (
          <div style={{ marginTop: 18, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-warning)' }}>
              Filters injected from schema:
            </span>
            {condTables.map((t) => (
              <span
                key={t.TABLE_NAME}
                style={{
                  fontFamily: MONO_FONT, fontSize: 13, padding: '3px 9px', borderRadius: 6,
                  background: 'var(--color-warning-weak)',
                  border: '1px solid var(--color-warning)',
                  color: 'var(--color-warning)', maxWidth: 340,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}
                title={`${t.TABLE_NAME}: ${t.CONDITION}`}
              >
                {t.TABLE_NAME}: {t.CONDITION}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Right: selected column properties */}
      <div style={{
        width: 320, minWidth: 280, flexShrink: 0, minHeight: 0, overflowY: 'auto',
        borderLeft: '1px solid var(--color-border)', padding: '16px 14px 40px', background: 'var(--color-bg)',
      }}>
        <ColumnProperties
          model={model}
          mode={mode}
          selection={selection}
          catalogEntry={catalogEntry}
          findGroup={findGroup}
          onColumnFieldChange={onColumnFieldChange}
          onMoveColumn={onMoveColumn}
          fnCatalog={fnCatalog}
        />
      </div>
    </>
  )
}

// ── Column chip (placed in a group) ───────────────────────────────
function ColumnChip({
  col, selected, onSelect, onRemove, onDragStart, onDragEnd,
}: {
  col: VisualColumn
  selected: boolean
  onSelect: () => void
  onRemove: () => void
  onDragStart: () => void
  onDragEnd: () => void
}) {
  return (
    <div
      className="vdx-colchip"
      draggable
      onDragStart={(e) => { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', String(col.COLID)); onDragStart() }}
      onDragEnd={onDragEnd}
      onClick={onSelect}
      style={selected ? {
        background: 'var(--color-accent-weak)',
        borderColor: 'var(--color-accent)',
      } : undefined}
    >
      <GripVertical size={12} style={{ opacity: 0.5, flexShrink: 0 }} />
      <span style={{
        flex: 1, fontFamily: MONO_FONT, fontSize: 14,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>
        {col.content || col.NAME || `Column ${col.COLID}`}
      </span>
      {col.EXPRESSIONTYPE === 'G'
        ? <AggBadge fn={col.FUNCTION || col.DEFAULTFUNCTION || 'SUM'} />
        : <TypeBadge type={col.type || 'string'} />}
      <button
        className="btn btn-ghost btn-sm"
        onClick={(e) => { e.stopPropagation(); onRemove() }}
        title="Remove from chart"
        style={{ padding: '2px 4px' }}
      >
        <Trash2 size={11} />
      </button>
    </div>
  )
}

function TypeBadge({ type }: { type: string }) {
  const isDate = (type || '').toLowerCase() === 'date'
  return (
    <span style={{
      fontSize: 13, fontWeight: 600, padding: '1px 7px', borderRadius: 5, flexShrink: 0,
      background: isDate ? 'var(--color-info-weak)' : 'var(--color-surface-2)',
      border: isDate
        ? '1px solid color-mix(in srgb, var(--color-info) 45%, transparent)'
        : '1px solid var(--color-border)',
      color: isDate ? 'var(--color-info)' : 'var(--color-text-faint)',
    }}>{type}</span>
  )
}
function AggBadge({ fn }: { fn: string }) {
  return (
    <span style={{
      fontSize: 13, fontWeight: 700, padding: '1px 7px', borderRadius: 5, flexShrink: 0,
      background: 'var(--color-success-weak)',
      border: '1px solid color-mix(in srgb, var(--color-success) 45%, transparent)',
      color: 'var(--color-success)',
    }} title={`Aggregated: ${fn}`}>{fn}</span>
  )
}

// ── Column properties (Layout right panel) ────────────────────────
function ColumnProperties({
  model, mode, selection, catalogEntry, findGroup, onColumnFieldChange, onMoveColumn,
  fnCatalog,
}: {
  model: VisualModel
  fnCatalog: VisualFunction[]
  mode: string
  selection: Selection
  catalogEntry: VisualCatalogEntry
  findGroup: (colId: number) => string | null
  onColumnFieldChange: (colId: number, field: keyof VisualColumn, value: unknown) => void
  onMoveColumn: (colId: number, std: string | null) => void
}) {
  const [formatOpen, setFormatOpen] = useState(false)
  if (selection.kind !== 'column') {
    return (
      <div style={{ ...emptyHintStyle, padding: '36px 14px', textAlign: 'center' }}>
        Select a placed column to edit how it appears.
      </div>
    )
  }
  let col: VisualColumn | undefined
  for (const g of model.LAYOUT_DATA.visualLayout.ColumnGroups || []) {
    const f = (g.COLUMNS || []).find((c) => c.COLID === selection.colId)
    if (f) { col = f; break }
  }
  if (!col) {
    return (
      <div style={{ ...emptyHintStyle, padding: '36px 14px', textAlign: 'center' }}>
        Select a placed column.
      </div>
    )
  }
  const isSchema = mode === 'sqlmodel'
  const currentGroup = findGroup(col.COLID)
  const c = col

  // ── Ranking-function context (window FUNCTION + its OVER_ORDER_BY) ────────
  const allModelColumns: VisualColumn[] = []
  for (const g of model.SQLMODEL.SQLModel?.COLUMNS || []) {
    for (const mc of g.COLUMN || []) if (mc?.NAME) allModelColumns.push(mc)
  }
  const isWindowFn = fnCatalog.some(
    (f) => f.name === (c.FUNCTION || '').toUpperCase() && f.kind === 'window',
  )
  const rawRankBy = Array.isArray(c.OVER_ORDER_BY)
    ? (c.OVER_ORDER_BY as { column?: string; direction?: string }[])[0]
    : undefined
  const rankBy = rawRankBy
    ? { column: String(rawRankBy.column || ''), direction: String(rawRankBy.direction || 'DESC') }
    : undefined

  // ── Column formatting context ──────────────────────────────────────────────
  const display = c.display as DisplayFormat | undefined
  const isCalc = c.CALC_TYPE === 'Presentation'
  const groupLc = (currentGroup || '').toLowerCase()
  const isPivot = catalogEntry.ID === 'pivot'
  const isValuesGroup = groupLc === 'values' || groupLc === 'measures'
  const showPivotAgg = isPivot && isValuesGroup && !isCalc
  const pivotAgg = String(c.PIVOT_AGG || '')
  const showDirection = showPivotAgg && POSITIONAL_PIVOT_AGGS.has(pivotAgg)

  // Adapter: row data is keyed by NAME, so db_name = NAME makes companion
  // pickers + the live preview inside DisplayFormatEditor resolve.
  const formatColumn: ColumnDef = {
    name: c.NAME,
    db_name: c.NAME,
    heading: c.content || c.NAME,
    col_id: String(c.COLID),
    group_name: '',
    col_type: c.COLTYPE ?? 'CHAR',
    display,
  } as ColumnDef
  const formColumns: string[] = []
  for (const g of model.LAYOUT_DATA.visualLayout.ColumnGroups || []) {
    for (const gc of g.COLUMNS || []) if (gc.NAME) formColumns.push(gc.NAME)
  }

  return (
    <div>
      <SectionHeader>Column</SectionHeader>
      <Field label="Display label">
        <input
          type="text"
          value={c.content || ''}
          onChange={(e) => onColumnFieldChange(c.COLID, 'content', e.target.value)}
          style={inputStyle}
        />
      </Field>

      {isSchema ? (
        <>
          <Field label="Source">
            <input type="text" value={`${c.DBTABLE || ''}.${c.DBNAME || ''}`} readOnly style={{ ...inputStyle, opacity: 0.7, fontFamily: MONO_FONT }} />
          </Field>
          <Field label="Aggregation / function">
            <select
              value={c.FUNCTION || ''}
              onChange={(e) => {
                const fn = e.target.value
                const isWin = fnCatalog.some((f) => f.name === fn && f.kind === 'window')
                onColumnFieldChange(c.COLID, 'FUNCTION', fn)
                onColumnFieldChange(c.COLID, 'DEFAULTFUNCTION', fn)
                onColumnFieldChange(c.COLID, 'EXPRESSIONTYPE', fn ? 'G' : 'C')
                if (isWin) {
                  // A ranking column is numeric and needs the measure it
                  // ranks by — default to the first aggregated measure DESC.
                  onColumnFieldChange(c.COLID, 'COLTYPE', 'NUMBER')
                  onColumnFieldChange(c.COLID, 'type', 'number')
                  const hasSpec = Array.isArray(c.OVER_ORDER_BY) && (c.OVER_ORDER_BY as unknown[]).length > 0
                  if (!hasSpec) {
                    const measure = allModelColumns.find(
                      (m) => m.COLID !== c.COLID
                        && fnCatalog.some((f) => f.name === (m.FUNCTION || '').toUpperCase() && f.kind !== 'window'),
                    ) || allModelColumns.find((m) => m.COLID !== c.COLID)
                    if (measure) {
                      onColumnFieldChange(c.COLID, 'OVER_ORDER_BY',
                        [{ column: measure.NAME, direction: 'DESC' }])
                    }
                  }
                } else if (c.OVER_ORDER_BY) {
                  onColumnFieldChange(c.COLID, 'OVER_ORDER_BY', undefined)
                }
              }}
              style={selectStyle}
            >
              <option value="">(none — plain column)</option>
              <optgroup label="Aggregate">
                {fnCatalog.filter((f) => f.kind === 'aggregate').map((f) => (
                  <option key={f.name} value={f.name}>{f.label}</option>
                ))}
              </optgroup>
              {fnCatalog.some((f) => f.kind === 'statistical') && (
                <optgroup label="Statistical">
                  {fnCatalog.filter((f) => f.kind === 'statistical').map((f) => (
                    <option key={f.name} value={f.name}>{f.label}</option>
                  ))}
                </optgroup>
              )}
              {fnCatalog.some((f) => f.kind === 'window') && (
                <optgroup label="Ranking">
                  {fnCatalog.filter((f) => f.kind === 'window').map((f) => (
                    <option key={f.name} value={f.name}>{f.label}</option>
                  ))}
                </optgroup>
              )}
            </select>
          </Field>
          {isWindowFn && (
            <Field label="Rank by">
              <div style={{ display: 'flex', gap: 6 }}>
                <select
                  value={rankBy?.column || ''}
                  onChange={(e) => onColumnFieldChange(c.COLID, 'OVER_ORDER_BY',
                    [{ column: e.target.value, direction: rankBy?.direction || 'DESC' }])}
                  style={{ ...selectStyle, flex: 1, minWidth: 0 }}
                >
                  {rankBy?.column && !allModelColumns.some((m) => m.NAME === rankBy.column) && (
                    <option value={rankBy.column}>{rankBy.column}</option>
                  )}
                  {allModelColumns.filter((m) => m.COLID !== c.COLID).map((m) => (
                    <option key={m.COLID} value={m.NAME}>{m.content || m.name || m.NAME}</option>
                  ))}
                </select>
                <select
                  value={rankBy?.direction || 'DESC'}
                  onChange={(e) => onColumnFieldChange(c.COLID, 'OVER_ORDER_BY',
                    [{ column: rankBy?.column || '', direction: e.target.value }])}
                  style={selectStyle}
                >
                  <option value="DESC">highest = 1</option>
                  <option value="ASC">lowest = 1</option>
                </select>
              </div>
            </Field>
          )}
          <Field label="Type">
            <select
              value={c.COLTYPE || 'CHAR'}
              onChange={(e) => {
                const ct = e.target.value
                onColumnFieldChange(c.COLID, 'COLTYPE', ct)
                onColumnFieldChange(c.COLID, 'type', ct === 'NUMBER' ? 'number' : ct === 'DATE' ? 'date' : 'string')
              }}
              style={selectStyle}
            >
              {COLTYPE_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </Field>
        </>
      ) : (
        <>
          <Field label="SELECT alias">
            <input type="text" value={c.NAME || ''} readOnly style={{ ...inputStyle, opacity: 0.7, fontFamily: MONO_FONT }} />
          </Field>
          <Field label="Type">
            <select
              value={c.type || 'string'}
              onChange={(e) => {
                const t = e.target.value as 'string' | 'number' | 'date'
                onColumnFieldChange(c.COLID, 'type', t)
                onColumnFieldChange(c.COLID, 'COLTYPE', t === 'number' ? 'NUMBER' : t === 'date' ? 'DATE' : 'CHAR')
              }}
              style={selectStyle}
            >
              {TYPE_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </Field>
        </>
      )}

      <Field label="Display format">
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            type="text"
            value={display?.type && display.type !== 'text' ? display.type : 'Plain text'}
            readOnly
            style={{ ...inputStyle, opacity: 0.85, flex: 1 }}
          />
          <button type="button" onClick={() => setFormatOpen(true)} style={editBtnStyle}>Edit…</button>
        </div>
      </Field>

      {showPivotAgg && (
        <>
          <Field label="Pivot aggregation">
            <select
              value={pivotAgg}
              onChange={(e) => onColumnFieldChange(c.COLID, 'PIVOT_AGG', e.target.value || undefined)}
              style={selectStyle}
            >
              {PIVOT_AGG_OPTIONS.map((o) => <option key={o.value || '_none'} value={o.value}>{o.label}</option>)}
            </select>
          </Field>
          {showDirection && (
            <Field label="Direction">
              <select
                value={(c.PIVOT_AXIS as string) || 'columns'}
                onChange={(e) => onColumnFieldChange(c.COLID, 'PIVOT_AXIS', e.target.value)}
                style={selectStyle}
              >
                <option value="columns">Across columns</option>
                <option value="rows">Down rows</option>
              </select>
            </Field>
          )}
          <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', margin: '2px 2px 10px', lineHeight: 1.5 }}>
            Computed in the pivot from the fetched rows; the Aggregation above controls how the database groups rows.
          </div>
        </>
      )}

      <Field label="Width">
        <input
          type="number"
          value={c.WIDTH ?? 100}
          onChange={(e) => onColumnFieldChange(c.COLID, 'WIDTH', Number(e.target.value))}
          style={inputStyle}
        />
      </Field>
      <Field label="Placement (group)">
        <select
          value={currentGroup || ''}
          onChange={(e) => onMoveColumn(c.COLID, e.target.value || null)}
          style={selectStyle}
        >
          <option value="">(remove from chart)</option>
          {catalogEntry.ColumnGroups.map((g) => (
            <option key={g.StandardName} value={g.StandardName}>
              {g.GroupName}{g.AllowedColumnTypes !== 'ANY' ? ` (${g.AllowedColumnTypes} only)` : ''}
            </option>
          ))}
        </select>
      </Field>

      <DisplayFormatEditor
        open={formatOpen}
        onClose={() => setFormatOpen(false)}
        column={formatColumn}
        formColumns={formColumns}
        onSave={(d) => onColumnFieldChange(c.COLID, 'display', d)}
      />
    </div>
  )
}

const editBtnStyle: React.CSSProperties = {
  padding: '5px 12px', fontSize: 13, borderRadius: 5, cursor: 'pointer', whiteSpace: 'nowrap',
  border: '1px solid var(--color-border)', background: 'var(--color-bg)', color: 'var(--color-text)',
}

// ════════════════════════════════════════════════════════════════
// Small shared primitives
// ════════════════════════════════════════════════════════════════
/** Collapsible numbered-section header (Data Source tab). Collapsed, it keeps
 *  the picked value visible as a chip so the choice is never hidden. */
function SectionToggleHeader({
  label, badge, open, summary, onToggle,
}: {
  label: string
  badge?: React.ReactNode
  open: boolean
  summary?: string
  onToggle: () => void
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      title={open ? 'Collapse section' : 'Expand section'}
      style={{
        display: 'flex', alignItems: 'center', gap: 8, width: '100%',
        border: 'none', background: 'transparent', padding: '2px 0',
        cursor: 'pointer', textAlign: 'left', minWidth: 0,
      }}
    >
      {open
        ? <ChevronDown size={14} style={{ flexShrink: 0, color: 'var(--color-text-secondary)' }} />
        : <ChevronRight size={14} style={{ flexShrink: 0, color: 'var(--color-text-secondary)' }} />}
      <span style={eyebrowStyle}>{label}</span>
      {badge}
      {!open && summary && <span style={summaryChipStyle}>{summary}</span>}
    </button>
  )
}

const summaryChipStyle: React.CSSProperties = {
  fontSize: 13.5, fontWeight: 600, padding: '2px 10px', borderRadius: 999,
  background: 'var(--color-accent-weak)', color: 'var(--color-accent)',
  border: '1px solid color-mix(in srgb, var(--color-accent) 40%, transparent)',
  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 340,
}

/** Expand-all / collapse-all icon pair for a folder tree header. */
function FolderExpandControls({ onExpandAll, onCollapseAll, expandTitle = 'Expand all folders', collapseTitle = 'Collapse all folders' }: {
  onExpandAll: () => void
  onCollapseAll: () => void
  expandTitle?: string
  collapseTitle?: string
}) {
  const btn: React.CSSProperties = {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: 22, height: 22, padding: 0, border: '1px solid var(--color-border)',
    borderRadius: 5, background: 'var(--color-bg)', cursor: 'pointer',
    color: 'var(--color-text-secondary)',
  }
  return (
    <span style={{ display: 'inline-flex', gap: 4, flexShrink: 0 }}>
      <button type="button" onClick={onExpandAll} title={expandTitle} style={btn}>
        <ChevronsUpDown size={13} />
      </button>
      <button type="button" onClick={onCollapseAll} title={collapseTitle} style={btn}>
        <ChevronsDownUp size={13} />
      </button>
    </span>
  )
}

function ModeButton({ active, label, hint, onClick }: { active: boolean; label: string; hint: string; onClick: () => void }) {
  return (
    <button
      className="vdx-pickcard"
      onClick={onClick}
      style={{
        flex: 1,
        ...(active ? {
          background: 'var(--color-accent-weak)',
          borderColor: 'var(--color-accent)',
          boxShadow: 'inset 0 0 0 0.5px var(--color-accent)',
        } : undefined),
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5 }}>
        <span style={{ fontWeight: 700, fontSize: 15 }}>{label}</span>
        <CheckCircle active={active} />
      </div>
      <div style={{ fontSize: 14.5, color: 'var(--color-text-secondary)', lineHeight: 1.5 }}>{hint}</div>
    </button>
  )
}

/** Business-area picker card (§8.3.3 area cards row). */
const AREA_DOT_COLORS = ['#4c8dff', '#3fb950', '#d29922', '#a371f7', '#db61a2', '#56d4dd']
function AreaCard({
  name, description, dotColor, active, tableCount, onPick,
}: {
  name: string
  description: string
  dotColor: string
  active: boolean
  tableCount?: number
  onPick: () => void
}) {
  return (
    <button
      className="vdx-pickcard"
      onClick={onPick}
      style={{
        width: 232, flexShrink: 0,
        ...(active ? {
          background: 'var(--color-accent-weak)',
          borderColor: 'var(--color-accent)',
          boxShadow: 'inset 0 0 0 0.5px var(--color-accent)',
        } : undefined),
      }}
      title={description}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5, minWidth: 0 }}>
        <span style={{ width: 9, height: 9, borderRadius: '50%', background: dotColor, flexShrink: 0 }} />
        <span style={{
          fontWeight: 700, fontSize: 15, minWidth: 0,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {name}
        </span>
        <CheckCircle active={active} />
      </div>
      <div style={{
        fontSize: 14, color: 'var(--color-text-secondary)', lineHeight: 1.45,
        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
        minHeight: 34,
      }}>
        {description}
      </div>
      {tableCount != null && (
        <div style={{ fontSize: 14, color: 'var(--color-text-faint)', marginTop: 6 }}>
          {tableCount} {tableCount === 1 ? 'table' : 'tables'}
        </div>
      )}
    </button>
  )
}

/** Right-aligned picked indicator shared by the choice cards. */
function CheckCircle({ active }: { active: boolean }) {
  return (
    <span
      aria-hidden
      style={{
        marginLeft: 'auto', width: 17, height: 17, borderRadius: '50%', flexShrink: 0,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 13, fontWeight: 700, lineHeight: 1,
        background: active ? 'var(--color-accent)' : 'transparent',
        border: active ? 'none' : '1.5px solid var(--color-border-strong)',
        color: active ? '#fff' : 'transparent',
      }}
    >✓</span>
  )
}

/** Spec §0.3 toggle switch — 42×24 pill, accent when on. */
function ToggleSwitch({ on, onToggle, title }: { on: boolean; onToggle: () => void; title?: string }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      onClick={onToggle}
      title={title}
      style={{
        width: 42, height: 24, borderRadius: 12, border: 'none', padding: 0,
        background: on ? 'var(--color-accent)' : 'var(--color-border-strong)',
        position: 'relative', cursor: 'pointer', flexShrink: 0,
      }}
    >
      <span style={{
        position: 'absolute', top: 3, left: on ? 21 : 3, width: 18, height: 18,
        borderRadius: '50%', background: '#fff', transition: 'left .15s',
        boxShadow: '0 1px 2px rgba(0,0,0,0.25)',
      }} />
    </button>
  )
}

function SectionHeader({ children, actions }: { children: React.ReactNode; actions?: React.ReactNode }) {
  return (
    <div style={{
      ...eyebrowStyle,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      marginTop: 4, marginBottom: 8,
    }}>
      <span style={{ minWidth: 0 }}>{children}</span>
      {actions}
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={fieldLabelStyle}>{label}</div>
      {children}
    </div>
  )
}

/** SQL / identifier font per spec §0.3. */
const MONO_FONT = "var(--font-mono, 'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, monospace)"

// Hover affordances need real CSS classes (inline styles can't hover). Base
// borders live here so :hover wins; active/selected states override inline.
const VDX_CSS = `
.vdx-bankchip {
  display: flex; align-items: center; gap: 7px; padding: 7px 9px;
  border: 1px dashed var(--color-border-strong); border-radius: 7px;
  margin-bottom: 6px; background: var(--color-bg-secondary);
  color: var(--color-text); cursor: grab; user-select: none; min-width: 0;
}
.vdx-bankchip:hover { border-color: var(--color-accent); }
.vdx-colchip {
  display: flex; align-items: center; gap: 7px; padding: 7px 9px;
  border: 1px solid var(--color-border); border-radius: 7px;
  margin-bottom: 6px; background: var(--color-raised);
  color: var(--color-text); cursor: grab; user-select: none; min-width: 0;
}
.vdx-colchip:hover { border-color: var(--color-border-strong); }
.vdx-pickcard {
  text-align: left; padding: 13px 15px;
  border: 1px solid var(--color-border); border-radius: 12px;
  background: var(--color-bg-secondary); color: var(--color-text);
  cursor: pointer; font: inherit; min-width: 0;
}
.vdx-pickcard:hover { border-color: var(--color-border-strong); }
`

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 11px', fontSize: 15.5,
  border: '1px solid var(--color-border)', borderRadius: 8,
  background: 'var(--color-input-bg)', color: 'var(--color-text)',
  fontFamily: 'inherit',
}
const selectStyle: React.CSSProperties = { ...inputStyle }
const fieldLabelStyle: React.CSSProperties = {
  fontSize: 13, fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: 4,
}
const dropTargetStyle: React.CSSProperties = {
  border: '1.5px dashed var(--color-border-strong)', borderRadius: 11,
  background: 'var(--color-bg-secondary)',
}
/** Bordered surface panel (spec §0.3 cards/panels). */
const panelStyle: React.CSSProperties = {
  border: '1px solid var(--color-border)', borderRadius: 11,
  background: 'var(--color-bg-secondary)',
}
const groupHeaderStyle: React.CSSProperties = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
  gap: 8, marginBottom: 8,
}
/** Step helper copy under an eyebrow (spec 13px dim body). */
const helperStyle: React.CSSProperties = {
  fontSize: 15, color: 'var(--color-text-secondary)', marginTop: 5, lineHeight: 1.55,
}
const hintStyle: React.CSSProperties = {
  fontSize: 14, color: 'var(--color-text-secondary)', marginTop: 7, lineHeight: 1.55,
}
const emptyHintStyle: React.CSSProperties = {
  fontSize: 14, color: 'var(--color-text-faint)', fontStyle: 'italic', padding: '4px 6px',
}
const tokenChipStyle: React.CSSProperties = {
  fontSize: 13, fontFamily: MONO_FONT, padding: '2px 9px', borderRadius: 5,
  background: 'var(--color-surface-2)', border: '1px solid var(--color-border)',
  color: 'var(--color-text-secondary)',
}
/** Green "New" micro-badge next to the step-1 eyebrow (§8.3.3). */
const newBadgeStyle: React.CSSProperties = {
  fontSize: 13, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase',
  padding: '1px 8px', borderRadius: 6, whiteSpace: 'nowrap', flexShrink: 0,
  background: 'var(--color-success-weak)', color: 'var(--color-success)',
}
// Per-option status badges: which options the user has customized vs. which
// still track the catalog default.
const badgeBaseStyle: React.CSSProperties = {
  fontSize: 13, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase',
  padding: '1px 7px', borderRadius: 5, whiteSpace: 'nowrap', flexShrink: 0,
}
const badgeCustomStyle: React.CSSProperties = {
  ...badgeBaseStyle, color: 'var(--color-accent)',
  background: 'var(--color-accent-weak)',
  border: '1px solid color-mix(in srgb, var(--color-accent) 40%, transparent)',
}
const badgeDefaultStyle: React.CSSProperties = {
  ...badgeBaseStyle, color: 'var(--color-text-secondary)',
  background: 'var(--color-surface-2)', border: '1px solid var(--color-border)',
}

// ════════════════════════════════════════════════════════════════
// Options tab — shows every option applicable to the selected visual
// type by default, each with a Customized/Default badge
// ════════════════════════════════════════════════════════════════
/** Evaluate an option's `showWhen` ("otherOption:val1,val2") against the live
 *  option values — an option with no rule is always shown. */
function optionVisible(opt: VisualOption, byName: Map<string, string>): boolean {
  const rule = (opt as { showWhen?: string }).showWhen
  if (!rule) return true
  const idx = rule.indexOf(':')
  if (idx < 0) return true
  const dep = rule.slice(0, idx).trim()
  const wanted = rule.slice(idx + 1).split(',').map((s) => s.trim().toLowerCase()).filter(Boolean)
  const current = (byName.get(dep) ?? '').toLowerCase()
  return wanted.includes(current)
}

function OptionsTab({
  model, catalogEntry, bank, showOverridesOnly, setShowOverridesOnly, onOptionChange,
}: {
  model: VisualModel
  catalogEntry: VisualCatalogEntry
  bank: BankColumn[]
  showOverridesOnly: boolean
  setShowOverridesOnly: (v: boolean) => void
  onOptionChange: (optionId: string, value: string) => void
}) {
  const liveOptions = model.LAYOUT_DATA.visualLayout.options || []
  const defaults = new Map(catalogEntry.options.map((o) => [o.optionName, o.defaultValue]))
  const liveByName = new Map(liveOptions.map((o) => [o.optionName, o.defaultValue ?? '']))
  const columns = bank.map((b) => ({ name: b.name, label: b.label || b.name }))
  const shown = liveOptions.filter((o) => optionVisible(o, liveByName))
  const overrides = shown.filter((o) => (defaults.get(o.optionName) ?? '') !== (o.defaultValue ?? ''))
  const visible = showOverridesOnly ? overrides : shown
  const hiddenAtDefault = shown.length - overrides.length

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, minWidth: 0 }}>
          <SectionHeader>Options · {catalogEntry.VisualName}</SectionHeader>
          {shown.length > 0 && (
            <span style={{ fontSize: 14, color: 'var(--color-text-secondary)', whiteSpace: 'nowrap' }}>
              {overrides.length} customized · {hiddenAtDefault} default
            </span>
          )}
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, cursor: 'pointer', flexShrink: 0 }}>
          <input type="checkbox" checked={showOverridesOnly} onChange={(e) => setShowOverridesOnly(e.target.checked)} />
          Overrides only
          {showOverridesOnly && hiddenAtDefault > 0 && (
            <span style={{ fontSize: 14, color: 'var(--color-text-secondary)' }}>({hiddenAtDefault} at default)</span>
          )}
        </label>
      </div>

      {/* A choropleth draws shapes from downloaded boundary files — surface
          whether THIS visual's country is available, and let the designer
          fetch it without leaving the page. */}
      {(liveByName.get('mapMode') || '').toLowerCase() === 'choropleth' && (
        <MapBoundaryStatus
          level={liveByName.get('geoLevel') || 'auto'}
          country={liveByName.get('geoCountry') || ''}
        />
      )}

      {visible.length === 0 && (
        <div style={emptyHintStyle}>
          {showOverridesOnly
            ? 'No options overridden — all are using the catalog defaults.'
            : 'This visual exposes no configurable options.'}
        </div>
      )}

      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
        gap: 14, maxWidth: 1180,
      }}>
        {visible.map((opt) => {
          const lov = (opt.listOfValues || '').split(',').map((s) => s.trim()).filter(Boolean)
          const isOverride = (defaults.get(opt.optionName) ?? '') !== (opt.defaultValue ?? '')
          return (
            <div key={opt.optionId} style={{
              padding: '13px 14px', border: '1px solid var(--color-border)',
              borderLeft: isOverride ? '3px solid var(--color-accent)' : '1px solid var(--color-border)',
              borderRadius: 12, background: 'var(--color-bg-secondary)',
              // A `code` option holds a whole document (the XSL stylesheet) —
              // in a 260px grid cell it is unusable, so give it the full row.
              ...(opt.optionType === 'code' ? { gridColumn: '1 / -1' } : null),
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, marginBottom: 5 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 7, minWidth: 0 }}>
                  <span style={{
                    fontSize: 14, fontWeight: 600, minWidth: 0,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {opt.optionHeading || opt.optionName}
                  </span>
                  <span style={isOverride ? badgeCustomStyle : badgeDefaultStyle}>
                    {isOverride ? 'Customized' : 'Default'}
                  </span>
                </div>
                {isOverride && (
                  <button
                    className="btn btn-ghost btn-sm"
                    style={{ padding: '0 4px', fontSize: 13, flexShrink: 0 }}
                    onClick={() => onOptionChange(opt.optionId, defaults.get(opt.optionName) ?? '')}
                    title="Reset to catalog default"
                  >reset</button>
                )}
              </div>
              <div style={{ fontSize: 14, color: 'var(--color-text-faint)', marginBottom: 8, fontFamily: MONO_FONT }}>
                {opt.optionName} · default: {defaults.get(opt.optionName) || '(empty)'}
              </div>
              <OptionInput opt={opt} lov={lov} columns={columns} onChange={onOptionChange} />
            </div>
          )
        })}
      </div>
    </div>
  )
}

function OptionInput({ opt, lov, columns, onChange }: {
  opt: VisualOption; lov: string[]; columns: { name: string; label: string }[]
  onChange: (id: string, v: string) => void
}) {
  // Column pickers source from the visual's whole column universe (the layout
  // bank) — the picked column need NOT be placed on a card slot.
  if (opt.optionType === 'column') {
    return (
      <select value={opt.defaultValue || ''} onChange={(e) => onChange(opt.optionId, e.target.value)} style={selectStyle}>
        <option value="">— auto —</option>
        {columns.map((c) => <option key={c.name} value={c.name}>{c.label}</option>)}
      </select>
    )
  }
  if (opt.optionType === 'column-multi') {
    const selected = new Set((opt.defaultValue || '').split(',').map((s) => s.trim()).filter(Boolean))
    const toggle = (name: string) => {
      const next = new Set(selected)
      if (next.has(name)) next.delete(name)
      else next.add(name)
      onChange(opt.optionId, columns.filter((c) => next.has(c.name)).map((c) => c.name).join(','))
    }
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3, maxHeight: 160, overflowY: 'auto' }}>
        {columns.length === 0 && <span style={{ fontSize: 14, color: 'var(--color-text-secondary)' }}>No columns available yet.</span>}
        {columns.map((c) => (
          <label key={c.name} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, cursor: 'pointer' }}>
            <input type="checkbox" checked={selected.has(c.name)} onChange={() => toggle(c.name)} />
            {c.label}
          </label>
        ))}
      </div>
    )
  }
  if (opt.dataType === 'boolean' && lov.length === 0) {
    // Spec §0.3 toggle switch — same stored 'true'/'false' values as before.
    const on = (opt.defaultValue || '').trim().toLowerCase() === 'true'
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
        <ToggleSwitch
          on={on}
          onToggle={() => onChange(opt.optionId, on ? 'false' : 'true')}
          title={opt.optionHeading || opt.optionName}
        />
        <span style={{ fontSize: 14.5, color: 'var(--color-text-secondary)' }}>{on ? 'On' : 'Off'}</span>
      </div>
    )
  }
  if (lov.length > 0) {
    return (
      <select value={opt.defaultValue || ''} onChange={(e) => onChange(opt.optionId, e.target.value)} style={selectStyle}>
        {lov.map((v) => <option key={v} value={v}>{v}</option>)}
      </select>
    )
  }
  // `optionType: 'code'` holds a whole document (the XSL visual's stylesheet),
  // not a scalar — a one-line <input> can't be used to author one.
  if (opt.optionType === 'code') {
    return (
      <textarea
        value={opt.defaultValue || ''}
        onChange={(e) => onChange(opt.optionId, e.target.value)}
        spellCheck={false}
        rows={12}
        placeholder={'<?xml version="1.0"?>\n<xsl:stylesheet version="1.0" xmlns:xsl="http://www.w3.org/1999/XSL/Transform">\n  <xsl:template match="/rows">\n    <table>\n      <xsl:for-each select="row"> … </xsl:for-each>\n    </table>\n  </xsl:template>\n</xsl:stylesheet>'}
        style={{
          ...inputStyle,
          minHeight: 190,
          resize: 'vertical',
          fontFamily: 'var(--font-mono, ui-monospace, SFMono-Regular, Menlo, monospace)',
          fontSize: 13.5,
          lineHeight: 1.5,
          whiteSpace: 'pre',
          overflowWrap: 'normal',
          overflowX: 'auto',
        }}
      />
    )
  }
  return (
    <input
      type={opt.dataType === 'number' ? 'number' : 'text'}
      value={opt.defaultValue || ''}
      onChange={(e) => onChange(opt.optionId, e.target.value)}
      style={inputStyle}
    />
  )
}
